import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Porch } from "building-types/porches/porch";
import { PorchKeyMeasuresGeometryType } from "building-types/porches/porchKeyMeasuresTypes";
import type { ExteriorMaterialOverrideWithKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofPerimeterRail, RoofRailConerInfo } from "building-types/roofs/roofPerimeterTypes";
import {
  RoofRailEndCondition,
  RoofPerimeterRailType,
  RoofRailCornerType,
} from "building-types/roofs/roofPerimeterTypes";
import type {
  ConnectedRoofTrimRailFilter,
  FasciaRail,
  FasciaTrimRail,
  FriezeBoardRail,
  FriezePlaneGroup,
  RoofTrimRail,
  RoofTrimRailBox,
  RoofTrimRailConstructor,
  RoofTrimRailFilter,
  RoofTrimRailGetExtraRails,
  RoofTrimRailGetKey,
  RoofTrimRailTransformer,
  RoofTrimRailVoid,
  SoffitRail,
} from "building-types/roofs/roofTrimTypes";
import {
  ROOF_RAIL_COLLINEARITY_TOLERANCE,
  ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
  ROOF_RAIL_RTREE_BOX_HALF_WIDTH_FOR_INTERSECTION,
  friezePlaneGroupNew,
  isNonVoidRoofTrimRailType,
  isRoofTrimRailTypeFascia,
  isRoofTrimRailTypeFrieze,
  isRoofTrimRailTypeSoffit,
  isRoofTrimRailTypeVoid,
  roofFasciaRailNew,
  roofFasciaTrimRailNew,
  roofFriezeBoardRailNew,
  roofSoffitRailNew,
  roofTrimRailBoxNew,
  roofTrimRailVoidNew,
} from "building-types/roofs/roofTrimTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { type ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import {
  EaveCutType,
  EaveFriezeBoardMode,
  EaveType,
  RakeFriezeBoardMode,
  WrapFriezeBoardMode,
} from "building-types/settings/settingsTypesEnum";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import { FASCIA_THICKNESS, FASCIA_TOP_TRIM_THICKNESS } from "common/constants/roofTrimConstants";
import type { RWTable, Table } from "common/data/table";
import { assertIsArray, assertIsNotNullOrUndefined, assertIsNumber } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { isBoxEave, isAngledEave, isOpenEave } from "doc/fasciaTopTrimSizeToValue";
import { DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE, DEFAULT_EAVE_RETURN_DISTANCE } from "doc/settings/defaultSettings";
import { supportsEaveReturnDistance } from "doc/settingsTools";
import { MULTIGABLE_BSP_EPSILON, FRIEZE_RAIL_SEARCH_BOX_TOLERANCE } from "doc/utils/roofTools";
import { aabb2ByTwoPoints } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3ByTwoPoints } from "geom/aabb3";
import { type Face, faceByNPoints, faceOrNullReverse, faceOrNullCopy, faceNew } from "geom/brep";
import { addConvexPlanesToBSPLeaf, intersectionLinePlaneBSP, isPointInsideBSP } from "geom/bsp";
import type { ClipRailContext, ClippingNode } from "geom/bspClipping";
import { clipRail3, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PlaneBSP } from "geom/bspTypes";
import { COMPACTNESS_EPSILON, DIST_EPSILON, Line2Side, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { intersectPlanePlane, intersectPolyLine2PolyLine2 } from "geom/intersect";
import { CurveCurvePointIntersection, CurveCurveRangeIntersection } from "geom/intersectLine2Line2";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import { line2Collinear, line2Side } from "geom/line2";
import { offsetPolyLine2, offsetPolyLine2Variable } from "geom/offset";
import {
  planeCopy,
  planeDistanceTo,
  planeIsCodirectional,
  planeIsCoplanar,
  planeIsParallel,
  planeMoveAlongNormal,
  planeNew,
  planeNewXY,
  planeNewXYNeg,
  planeNormal,
  planeProject3,
  planeProjectPolyLine3,
  planeReProjectPolyLine2,
  planeReverse,
  planeRotateAroundNormal,
  polyLine2OnPlane,
} from "geom/plane";
import { planeBSPByPointAndNormal, planeBSPMoveAlongNormal } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Extend,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3Concat,
  polyLine3Copy,
  polyLine3Edge,
  polyLine3EdgeCount,
  polyLine3EdgeSlopeInZ,
  polyLine3EdgeTangent,
  polyLine3EndPoint,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3MergeCoincidentVertices,
  polyLine3Move,
  polyLine3New,
  polyLine3Simplify,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { vec2DistSquared } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import {
  vec3Add,
  vec3AddScale,
  vec3AngleBetween,
  vec3Cross,
  vec3CrossNormalize,
  vec3Dist,
  vec3DistSquared,
  vec3Dot,
  vec3Negate,
  vec3Normalize,
  vec3Sub,
} from "geom/vec3";
import RBush from "rbush";
import { RBush3D } from "rbush-3d";
import {
  friezeBoardsTotalDepth,
  getFriezeBoardSize,
  hasValidFriezeBoards,
  roofHasCorner,
} from "../../elements/roofTrimUtility";
import {
  getRoofCornerInfo,
  getRoofPerimeterCornerInfo,
  getRoofPerimeterRailEndRafterDir,
  getRoofPerimeterRailStartRafterDir,
  isRoofPerimeterRailConnectedAtEnd,
  isRoofPerimeterRailConnectedAtStart,
  isRoofRailCorner,
} from "./roofPerimeterRails";
import { roofPerimeterRailNew, roofRailCornerInfoNew } from "./roofPerimeters";
import type { CoplanarRails } from "./roofRails";
import {
  reprojectCoplanarRailsArrayToWalls,
  groupCoplanarRails,
  rejoinCoplanarRails,
  coplanarRailsNew,
} from "./roofRails";
import { doesEdgeAlignsWithInputOrientation } from "./roofSideOverridesUtility";
import { buildExtraFriezeBoardRails } from "./roofTrimRailsTools";

const MAX_LEGACY_FRIEZE_BOARD_EXTENSION_DEPTH = 4;
const END_POINT_TOLERANCE = 2;
const FRIEZE_PORCH_HEADER_TOLERANCE = 0.75;
const FRIEZE_PORCH_HEADER_ANGLE_TOLERANCE = 0.01;
const FRIEZE_BOARD_MIN_LENGTH = 2;
const FRIEZE_BOARD_SLOPE_EPSILON = 0.01;

const FRIEZE_WRAP_MODE_HAS_SIDES: { [key in WrapFriezeBoardMode]?: boolean } = {
  [WrapFriezeBoardMode.SIDES]: true,
  [WrapFriezeBoardMode.FULL]: true,
};

function getEdgeEndPlaneBSP(edge: PolyLine2, planeOffset: number, isEnd: boolean): PlaneBSP {
  const [x, y] = isEnd ? polyLine2End(edge) : polyLine2Start(edge);

  const [nX, nY] = polyLine2EdgeTangent(edge, isEnd ? 0 : polyLine2EdgeCount(edge) - 1, !isEnd);

  return planeBSPMoveAlongNormal(planeBSPByPointAndNormal(x, y, 0, nX, nY, 0), planeOffset);
}

function supportGableEndDetail(
  { type, isStartEdge, isEndEdge, prevPerimeterRail, nextPerimeterRail }: RoofPerimeterRail,
  isEnd: boolean,
): boolean {
  if (type !== RoofPerimeterRailType.RAKE) {
    return false;
  }

  if (!isStartEdge && !isEndEdge) {
    return false;
  }

  return isEnd ? !!nextPerimeterRail : !!prevPerimeterRail;
}

function getFriezeBoardWrapMode(roofPerimeterRail: RoofPerimeterRail, isEnd: boolean): WrapFriezeBoardMode {
  if (!supportGableEndDetail(roofPerimeterRail, isEnd)) {
    return WrapFriezeBoardMode.NONE;
  }

  const { wrapFriezeBoardModeAtStart, wrapFriezeBoardModeAtEnd, eaveType, eaveCutTypeAtStart, eaveCutTypeAtEnd } =
    roofPerimeterRail;

  if (!isBoxEave[eaveType]) {
    return WrapFriezeBoardMode.NONE;
  }

  if (isEnd && eaveCutTypeAtEnd !== RoofRailEndCondition.EXTENDED) {
    return wrapFriezeBoardModeAtEnd;
  } else if (!isEnd && eaveCutTypeAtStart !== RoofRailEndCondition.EXTENDED) {
    return wrapFriezeBoardModeAtStart;
  } else {
    return WrapFriezeBoardMode.NONE;
  }
}

interface GetBoxedOpenFriezeBoardExtensionOptions {
  readonly friezeRail: PolyLine3;
  readonly soffitInnerTopRail: PolyLine3;
  readonly soffitInnerBottomRail: PolyLine3;
  readonly minHeight: number;
  readonly isEnd: boolean;
}

interface GetBoxedOpenFriezeBoardExtensionResult {
  readonly dist: number;
  readonly clipPlane?: Plane;
}

function getBoxedOpenFriezeBoardExtension({
  friezeRail,
  soffitInnerTopRail,
  soffitInnerBottomRail,
  minHeight,
  isEnd,
}: GetBoxedOpenFriezeBoardExtensionOptions): GetBoxedOpenFriezeBoardExtensionResult {
  const [x, y, z] = isEnd ? polyLine3EndPoint(friezeRail) : polyLine3StartPoint(friezeRail);
  const [soffitBottomX, soffitBottomY, soffitBottomZ] = isEnd
    ? polyLine3EndPoint(soffitInnerBottomRail)
    : polyLine3StartPoint(soffitInnerBottomRail);
  const [soffitTopX, soffitTopY] = isEnd
    ? polyLine3EndPoint(soffitInnerTopRail)
    : polyLine3StartPoint(soffitInnerTopRail);

  const int = intersectRay3PlaneWithNormal(
    x,
    y,
    z,
    soffitBottomX - x,
    soffitBottomY - y,
    soffitBottomZ - z,
    soffitTopX,
    soffitTopY,
    minHeight,
    0,
    0,
    1,
  );

  if (!int) {
    return { dist: 0 };
  }

  return {
    dist: Math.max(vec3Dist(x, y, z, int[0], int[1], int[2]), 0),
    clipPlane: planeNewXYNeg(0, 0, int[2]),
  };
}

interface BuildFriezeBoardSubRailsOptions {
  readonly baseRail: PolyLine3;
  readonly friezePlane: Plane;
  readonly roofPerimeterRail: RoofPerimeterRail;
  readonly startWrapMode: WrapFriezeBoardMode;
  readonly endWrapMode: WrapFriezeBoardMode;
  readonly startEaveReturnDistance: number;
  readonly endEaveReturnDistance: number;
  readonly friezeBoardSize: number;
  readonly isCoplanarEaveAtStart: boolean;
  readonly isCoplanarEaveAtEnd: boolean;
  readonly friezeBoardVerticalOffset: number;
  readonly hasStartCorner: boolean;
  readonly hasEndCorner: boolean;
  readonly eaveReturnFriezeBoardTotalDepth: number | undefined;
}

interface BuildFriezeBoardSubRailsResult {
  readonly friezeBoardRails: PolyLine3[];
  readonly hasStartExtension: boolean;
  readonly hasEndExtension: boolean;
}

function buildFriezeBoardSubRails({
  baseRail,
  friezePlane,
  roofPerimeterRail,
  startWrapMode,
  endWrapMode,
  startEaveReturnDistance,
  endEaveReturnDistance,
  friezeBoardSize,
  isCoplanarEaveAtStart,
  isCoplanarEaveAtEnd,
  friezeBoardVerticalOffset,
  hasStartCorner,
  hasEndCorner,
  eaveReturnFriezeBoardTotalDepth,
}: BuildFriezeBoardSubRailsOptions): BuildFriezeBoardSubRailsResult {
  const friezeBoardRails: PolyLine3[] = [];

  if (!baseRail) {
    return {
      friezeBoardRails,
      hasStartExtension: false,
      hasEndExtension: false,
    };
  }

  const {
    rail,
    soffitOffset,
    fasciaBoardSize,
    roofEdgeStartOverhang,
    roofEdgeEndOverhang,
    prevPerimeterRail,
    nextPerimeterRail,
    type,
    eaveType,
    baseRail: baseRailPerimeter,
  } = roofPerimeterRail;
  const soffitDepthRake = fasciaBoardSize - soffitOffset;
  const isRake = type === RoofPerimeterRailType.RAKE;

  const startSlope = polyLine3EdgeSlopeInZ(baseRail, 0);
  const endSlope = -polyLine3EdgeSlopeInZ(baseRail, polyLine3EdgeCount(baseRail) - 1);

  let startOffsetXNotNeeded: boolean = false;
  let endOffsetXNotNeeded: boolean = false;

  let coplanarOffsetAtStart: number = 0;
  let coplanarOffsetAtEnd: number = 0;

  if (isCoplanarEaveAtStart && startSlope !== 0 && prevPerimeterRail) {
    const { soffitOffset, fasciaBoardSize, baseRail: baseRailPreviousPerimeter } = prevPerimeterRail;
    const soffitDepthEaveStart = fasciaBoardSize - soffitOffset;
    const f2 = 1 / startSlope;
    const f1 = Math.sqrt(1 + startSlope * startSlope) * f2;
    coplanarOffsetAtStart = soffitDepthRake * f1 - soffitDepthEaveStart * f2;
    const [, , z1] = polyLine3StartPoint(baseRailPerimeter);
    const [, , z2] = polyLine3EndPoint(baseRailPreviousPerimeter);
    startOffsetXNotNeeded = Math.abs(z1 - z2) > 0.1;
  }

  if (isCoplanarEaveAtEnd && endSlope !== 0 && nextPerimeterRail) {
    const { soffitOffset, fasciaBoardSize, baseRail: baseRailNextPerimeter } = nextPerimeterRail;
    const soffitDepthEaveEnd = fasciaBoardSize - soffitOffset;
    const f2 = 1 / endSlope;
    const f1 = Math.sqrt(1 + endSlope * endSlope) * f2;
    coplanarOffsetAtEnd = soffitDepthRake * f1 - soffitDepthEaveEnd * f2;
    const [, , z1] = polyLine3EndPoint(baseRailPerimeter);
    const [, , z2] = polyLine3StartPoint(baseRailNextPerimeter);
    endOffsetXNotNeeded = Math.abs(z1 - z2) > 0.1;
  }

  const startExtension = !isCoplanarEaveAtStart ? roofEdgeStartOverhang ?? 0 : 0;
  const endExtension = !isCoplanarEaveAtEnd ? roofEdgeEndOverhang ?? 0 : 0;

  const friezeRail = polyLine3New(baseRail.length + 12);

  const baseRailElemCount = baseRail.length;
  const friezeRailElemCount = friezeRail.length;
  const { uX, uY, uZ, vX, vY, vZ } = friezePlane;

  const eaveTypeWithPossibleWrap = supportsEaveReturnDistance[eaveType];
  const applyStartOffsetX =
    isRake && eaveTypeWithPossibleWrap && !startOffsetXNotNeeded && FRIEZE_WRAP_MODE_HAS_SIDES[startWrapMode];

  const applyEndOffsetX =
    isRake && eaveTypeWithPossibleWrap && !endOffsetXNotNeeded && FRIEZE_WRAP_MODE_HAS_SIDES[endWrapMode];

  const startOffsetX = applyStartOffsetX ? startEaveReturnDistance - coplanarOffsetAtStart : 0;
  const endOffsetX = applyEndOffsetX ? endEaveReturnDistance - coplanarOffsetAtEnd : 0;

  const startRefHeight = rail[2] - soffitDepthRake;
  const endRefHeight = rail[rail.length - 1] - soffitDepthRake;

  const prevOffsetY = startRefHeight - baseRail[2];
  const nextOffsetY = endRefHeight - baseRail[baseRail.length - 1];

  let startOffsetY = startWrapMode !== WrapFriezeBoardMode.NONE ? prevOffsetY : 0;
  let endOffsetY = endWrapMode !== WrapFriezeBoardMode.NONE ? nextOffsetY : 0;

  if (startWrapMode === WrapFriezeBoardMode.SIDES && !isCoplanarEaveAtStart) {
    startOffsetY += friezeBoardSize;
    //if the wrap mode is sides and the eave return is not coplanar,
    //then we need to subtract the eave return frieze board total depth
    //this is to make sure the eave frieze board and rake frieze board
    //are aligned at the eave return
    if (hasStartCorner && eaveReturnFriezeBoardTotalDepth) {
      startOffsetY -= eaveReturnFriezeBoardTotalDepth;
    }
  }

  if (endWrapMode === WrapFriezeBoardMode.SIDES && !isCoplanarEaveAtEnd) {
    endOffsetY += friezeBoardSize;
    //if the wrap mode is sides and the eave return is not coplanar,
    //then we need to subtract the eave return frieze board total depth
    //this is to make sure the eave frieze board and rake frieze board
    //are aligned at the eave return
    if (hasEndCorner && eaveReturnFriezeBoardTotalDepth) {
      endOffsetY -= eaveReturnFriezeBoardTotalDepth;
    }
  }

  const startMidOffsetY = startSlope * startOffsetX;
  const endMidOffsetY = endSlope * endOffsetX;

  let startBottomRail: PolyLine3 | undefined;
  let endBottomRail: PolyLine3 | undefined;
  if (startWrapMode === WrapFriezeBoardMode.BOTTOM) {
    friezeRail[0] = baseRail[0];
    friezeRail[1] = baseRail[1];
    friezeRail[2] = baseRail[2];

    friezeRail[3] = baseRail[0];
    friezeRail[4] = baseRail[1];
    friezeRail[5] = baseRail[2];

    startBottomRail = polyLine3ByTwoPoints(
      baseRail[0] - uX * startExtension + vX * startOffsetY,
      baseRail[1] - uY * startExtension + vY * startOffsetY,
      baseRail[2] - uZ * startExtension + vZ * startOffsetY - friezeBoardVerticalOffset,
      baseRail[0] + uX * startOffsetX + vX * startOffsetY - uX * friezeBoardVerticalOffset,
      baseRail[1] + uY * startOffsetX + vY * startOffsetY - uY * friezeBoardVerticalOffset,
      baseRail[2] + uZ * startOffsetX + vZ * startOffsetY - uZ * friezeBoardVerticalOffset - friezeBoardVerticalOffset,
    );
  } else {
    if (startWrapMode === WrapFriezeBoardMode.SIDES) {
      friezeRail[0] = baseRail[0] + uX * (startOffsetX - 0.1) + vX * startOffsetY;
      friezeRail[1] = baseRail[1] + uY * (startOffsetX - 0.1) + vY * startOffsetY;
      friezeRail[2] = baseRail[2] + uZ * (startOffsetX - 0.1) + vZ * startOffsetY;

      friezeRail[3] = baseRail[0] + uX * startOffsetX + vX * startOffsetY;
      friezeRail[4] = baseRail[1] + uY * startOffsetX + vY * startOffsetY;
      friezeRail[5] = baseRail[2] + uZ * startOffsetX + vZ * startOffsetY;
    } else if (startWrapMode === WrapFriezeBoardMode.FULL) {
      friezeRail[0] = baseRail[0] - uX * startExtension + vX * startOffsetY;
      friezeRail[1] = baseRail[1] - uY * startExtension + vY * startOffsetY;
      friezeRail[2] = baseRail[2] - uZ * startExtension + vZ * startOffsetY - friezeBoardVerticalOffset;

      friezeRail[3] = baseRail[0] + uX * startOffsetX + vX * startOffsetY;
      friezeRail[4] = baseRail[1] + uY * startOffsetX + vY * startOffsetY;
      friezeRail[5] = baseRail[2] + uZ * startOffsetX + vZ * startOffsetY - friezeBoardVerticalOffset;
    } else if (startWrapMode === WrapFriezeBoardMode.NONE) {
      for (let i = 0; i < 6; i += 3) {
        friezeRail[i] = baseRail[0] + uX * startOffsetX + vX * startMidOffsetY;
        friezeRail[i + 1] = baseRail[1] + uY * startOffsetX + vY * startMidOffsetY;
        friezeRail[i + 2] = baseRail[2] + uZ * startOffsetX + vZ * startMidOffsetY;
      }
    }
  }

  friezeRail[6] = baseRail[0] + uX * startOffsetX + vX * startMidOffsetY;
  friezeRail[7] = baseRail[1] + uY * startOffsetX + vY * startMidOffsetY;
  friezeRail[8] = baseRail[2] + uZ * startOffsetX + vZ * startMidOffsetY;

  for (let i = 3, l = baseRailElemCount - 3; i < l; i += 3) {
    friezeRail[i + 6] = baseRail[i];
    friezeRail[i + 7] = baseRail[i + 1];
    friezeRail[i + 8] = baseRail[i + 2];
  }

  friezeRail[friezeRailElemCount - 9] = baseRail[baseRailElemCount - 3] - uX * endOffsetX + vX * endMidOffsetY;
  friezeRail[friezeRailElemCount - 8] = baseRail[baseRailElemCount - 2] - uY * endOffsetX + vY * endMidOffsetY;
  friezeRail[friezeRailElemCount - 7] = baseRail[baseRailElemCount - 1] - uZ * endOffsetX + vZ * endMidOffsetY;

  if (endWrapMode === WrapFriezeBoardMode.BOTTOM) {
    friezeRail[friezeRailElemCount - 6] = baseRail[baseRailElemCount - 3];
    friezeRail[friezeRailElemCount - 5] = baseRail[baseRailElemCount - 2];
    friezeRail[friezeRailElemCount - 4] = baseRail[baseRailElemCount - 1];

    friezeRail[friezeRailElemCount - 3] = baseRail[baseRailElemCount - 3];
    friezeRail[friezeRailElemCount - 2] = baseRail[baseRailElemCount - 2];
    friezeRail[friezeRailElemCount - 1] = baseRail[baseRailElemCount - 1];

    endBottomRail = polyLine3ByTwoPoints(
      baseRail[baseRailElemCount - 3] - uX * endOffsetX + vX * endOffsetY + uX * friezeBoardVerticalOffset,
      baseRail[baseRailElemCount - 2] - uY * endOffsetX + vY * endOffsetY + uY * friezeBoardVerticalOffset,
      baseRail[baseRailElemCount - 1] -
        uZ * endOffsetX +
        vZ * endOffsetY +
        uZ * friezeBoardVerticalOffset -
        friezeBoardVerticalOffset,
      baseRail[baseRailElemCount - 3] + uX * endExtension + vX * endOffsetY,
      baseRail[baseRailElemCount - 2] + uY * endExtension + vY * endOffsetY,
      baseRail[baseRailElemCount - 1] + uZ * endExtension + vZ * endOffsetY - friezeBoardVerticalOffset,
    );
  } else {
    if (endWrapMode === WrapFriezeBoardMode.SIDES) {
      friezeRail[friezeRailElemCount - 6] = baseRail[baseRailElemCount - 3] - uX * endOffsetX + vX * endOffsetY;
      friezeRail[friezeRailElemCount - 5] = baseRail[baseRailElemCount - 2] - uY * endOffsetX + vY * endOffsetY;
      friezeRail[friezeRailElemCount - 4] = baseRail[baseRailElemCount - 1] - uZ * endOffsetX + vZ * endOffsetY;

      friezeRail[friezeRailElemCount - 3] = baseRail[baseRailElemCount - 3] - uX * (endOffsetX - 0.1) + vX * endOffsetY;
      friezeRail[friezeRailElemCount - 2] = baseRail[baseRailElemCount - 2] - uY * (endOffsetX - 0.1) + vY * endOffsetY;
      friezeRail[friezeRailElemCount - 1] = baseRail[baseRailElemCount - 1] - uZ * (endOffsetX - 0.1) + vZ * endOffsetY;
    } else if (endWrapMode === WrapFriezeBoardMode.FULL) {
      friezeRail[friezeRailElemCount - 6] = baseRail[baseRailElemCount - 3] - uX * endOffsetX + vX * endOffsetY;
      friezeRail[friezeRailElemCount - 5] = baseRail[baseRailElemCount - 2] - uY * endOffsetX + vY * endOffsetY;
      friezeRail[friezeRailElemCount - 4] =
        baseRail[baseRailElemCount - 1] - uZ * endOffsetX + vZ * endOffsetY - friezeBoardVerticalOffset;

      friezeRail[friezeRailElemCount - 3] = baseRail[baseRailElemCount - 3] + uX * endExtension + vX * endOffsetY;
      friezeRail[friezeRailElemCount - 2] = baseRail[baseRailElemCount - 2] + uY * endExtension + vY * endOffsetY;
      friezeRail[friezeRailElemCount - 1] =
        baseRail[baseRailElemCount - 1] + uZ * endExtension + vZ * endOffsetY - friezeBoardVerticalOffset;
    } else if (endWrapMode === WrapFriezeBoardMode.NONE) {
      for (let i = friezeRailElemCount - 6; i < friezeRailElemCount; i += 3) {
        friezeRail[i] = baseRail[baseRailElemCount - 3] - uX * endOffsetX + vX * endMidOffsetY;
        friezeRail[i + 1] = baseRail[baseRailElemCount - 2] - uY * endOffsetX + vY * endMidOffsetY;
        friezeRail[i + 2] = baseRail[baseRailElemCount - 1] - uZ * endOffsetX + vZ * endMidOffsetY;
      }
    }
  }

  const addFriezeRail = (friezeRail: PolyLine3) => {
    if (!friezeRail) {
      return;
    }

    friezeBoardRails.push(polyLine3MergeCoincidentVertices(friezeRail));
  };

  if (startBottomRail !== undefined) {
    addFriezeRail(startBottomRail);
  }

  addFriezeRail(friezeRail);

  if (endBottomRail !== undefined) {
    addFriezeRail(endBottomRail);
  }

  return {
    friezeBoardRails,
    hasStartExtension: startWrapMode !== WrapFriezeBoardMode.NONE,
    hasEndExtension: endWrapMode !== WrapFriezeBoardMode.NONE,
  };
}

export function buildRoofTrimRailsRTree(
  trimRails: RoofTrimRail[],
  rtree: RBush3D,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): void {
  // helper function for adding a new point to the rtree
  const addEndPoint = (roofTrimRail: RoofTrimRail, isEnd: boolean) => {
    if (isRoofTrimRailTypeVoid(roofTrimRail)) {
      return;
    }
    const { rail } = roofTrimRail;

    const [x, y, z] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

    const bbox = aabb3ByTwoPoints(
      x - boxHalfSize,
      y - boxHalfSize,
      z - boxHalfSize,
      x + boxHalfSize,
      y + boxHalfSize,
      z + boxHalfSize,
    );

    rtree.insert(roofTrimRailBoxNew(bbox, roofTrimRail, isEnd));
  };
  for (const trimRail of trimRails) {
    addEndPoint(trimRail, false);
    addEndPoint(trimRail, true);
  }
}

export function buildRoofTrimRailsRTreeGroup(
  trimRailGroups: Table<RoofTrimRail[]>,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): RBush3D {
  const rtree = new RBush3D();

  // iterate groups
  for (const groupId in trimRailGroups) {
    const trimRails = trimRailGroups[groupId];

    // iterate rails
    buildRoofTrimRailsRTree(trimRails, rtree, boxHalfSize);
  }

  return rtree;
}

interface BuildTrimRailsOptions<T extends RoofTrimRail> {
  readonly roofRailsTable: Table<RoofPerimeterRail[]>;
  readonly filter: RoofTrimRailFilter;
  readonly getKey: RoofTrimRailGetKey;
  readonly constructor: RoofTrimRailConstructor<T>;
  readonly transformer?: RoofTrimRailTransformer<T>;
  readonly getExtraRails?: RoofTrimRailGetExtraRails<T>;
}

interface BuildTrimRailsResult<T extends RoofTrimRail> {
  trimRailsTable: Table<T[]>;
  rtree: RBush3D;
}

function buildTrimRails<T extends RoofTrimRail>({
  roofRailsTable,
  filter,
  getKey,
  constructor,
  transformer,
  getExtraRails,
}: BuildTrimRailsOptions<T>): BuildTrimRailsResult<T> {
  const trimRailsTable: RWTable<T[]> = {};

  const addTrimRails = (subRoofId: string, trimRails: T[]) => {
    if (!trimRailsTable[subRoofId]) {
      trimRailsTable[subRoofId] = [];
    }

    trimRailsTable[subRoofId].push(...trimRails);
  };

  // iterate roofs
  for (const subRoofId in roofRailsTable) {
    const roofRails = roofRailsTable[subRoofId];

    // apply filter
    const filteredRoofRails = roofRails.filter(filter);

    // iterate filtered roof rails
    for (const roofRail of filteredRoofRails) {
      // get the group id
      const groupId = getKey(roofRail);

      // rail construction
      const trimRails = constructor(roofRail, groupId);

      // add to the corresponding subroof
      addTrimRails(subRoofId, trimRails);
    }

    // add extra rails
    if (getExtraRails) {
      addTrimRails(subRoofId, getExtraRails(subRoofId));
    }
  }

  // build the rtree
  const rtree = buildRoofTrimRailsRTreeGroup(trimRailsTable);

  // rail transformation
  if (transformer) {
    for (const subRoofId in trimRailsTable) {
      const trimRails = trimRailsTable[subRoofId];
      trimRailsTable[subRoofId] = trimRails.map((x) => transformer(x, rtree));
    }
  }

  return {
    trimRailsTable,
    rtree,
  };
}

export function buildSoffitRails(roofRailsTable: Table<RoofPerimeterRail[]>): BuildTrimRailsResult<SoffitRail> {
  const soffitRailFilter = ({ includeSoffit }: RoofPerimeterRail): boolean => {
    return !!includeSoffit;
  };

  const soffitGetKey = ({ overhang, fasciaBoardSize, soffitOffset }: RoofPerimeterRail): string => {
    return `${overhang}_${fasciaBoardSize}_${soffitOffset}`;
  };

  const soffitRailConverter = (roofPerimeterRail: RoofPerimeterRail, groupId: string): SoffitRail[] => {
    const soffitRail = buildEnclosingRail(roofPerimeterRail, true, groupId);
    return isRoofTrimRailTypeSoffit(soffitRail) ? [soffitRail] : [];
  };

  return buildTrimRails<SoffitRail>({
    roofRailsTable,
    filter: soffitRailFilter,
    getKey: soffitGetKey,
    constructor: soffitRailConverter,
  });
}

export function buildExposedRaftersRails(roofRailsTable: Table<RoofPerimeterRail[]>): BuildTrimRailsResult<SoffitRail> {
  const soffitRailFilter = ({ includeExposedRafters }: RoofPerimeterRail): boolean => {
    return !!includeExposedRafters;
  };

  const soffitGetKey = ({ overhang, soffitOffset }: RoofPerimeterRail): string => {
    return `${overhang}_${soffitOffset}`;
  };

  const soffitRailConverter = (roofPerimeterRail: RoofPerimeterRail, groupId: string): SoffitRail[] => {
    const soffitRail = buildEnclosingRail(roofPerimeterRail, true, groupId);
    return isRoofTrimRailTypeSoffit(soffitRail) ? [soffitRail] : [];
  };

  return buildTrimRails<SoffitRail>({
    roofRailsTable,
    filter: soffitRailFilter,
    getKey: soffitGetKey,
    constructor: soffitRailConverter,
  });
}

export function buildFasciaRails(roofRailsTable: Table<RoofPerimeterRail[]>): BuildTrimRailsResult<FasciaRail> {
  const fasciaRailFilter = ({ includeFasciaBoard }: RoofPerimeterRail): boolean => {
    return !!includeFasciaBoard;
  };

  const fasciaGetKey = ({ fasciaBoardSize }: RoofPerimeterRail): string => {
    return fasciaBoardSize.toString();
  };

  const fasciaRailConverter = (roofPerimeterRail: RoofPerimeterRail, groupId: string): FasciaRail[] => {
    const {
      rail,
      fasciaBoardSize,
      prevPerimeterRail,
      nextPerimeterRail,
      fasciaMaterial,
      fasciaBoardThickness,
      adjustFasciaBoardSize,
    } = roofPerimeterRail;
    const prevBoardSize = prevPerimeterRail?.fasciaBoardSize ?? fasciaBoardSize;
    const nextBoardSize = nextPerimeterRail?.fasciaBoardSize ?? fasciaBoardSize;

    return [
      roofFasciaRailNew(
        rail,
        roofPerimeterRail,
        fasciaBoardSize,
        prevBoardSize,
        nextBoardSize,
        fasciaBoardThickness,
        adjustFasciaBoardSize,
        groupId,
        fasciaMaterial,
      ),
    ];
  };

  return buildTrimRails<FasciaRail>({
    roofRailsTable,
    filter: fasciaRailFilter,
    getKey: fasciaGetKey,
    constructor: fasciaRailConverter,
  });
}

export function buildFasciaTopTrimRails(
  roofRailsTable: Table<RoofPerimeterRail[]>,
): BuildTrimRailsResult<FasciaTrimRail> {
  const fasciaTopTrimRailFilter = ({ includeFasciaTopTrim }: RoofPerimeterRail): boolean => {
    return !!includeFasciaTopTrim;
  };

  const fasciaTopTrimGetKey = ({ fasciaTopTrimSize }: RoofPerimeterRail): string => {
    return fasciaTopTrimSize.toString();
  };

  const fasciaTopTrimRailConverter = (roofPerimeterRail: RoofPerimeterRail, groupId: string): FasciaTrimRail[] => {
    const { rail, fasciaTopTrimSize, prevPerimeterRail, nextPerimeterRail, fasciaTopTrimMaterial } = roofPerimeterRail;
    const prevBoardSize = prevPerimeterRail?.fasciaTopTrimSize ?? fasciaTopTrimSize;
    const nextBoardSize = nextPerimeterRail?.fasciaTopTrimSize ?? fasciaTopTrimSize;

    return [
      roofFasciaTrimRailNew(
        rail,
        roofPerimeterRail,
        fasciaTopTrimSize,
        prevBoardSize,
        nextBoardSize,
        FASCIA_TOP_TRIM_THICKNESS,
        groupId,
        fasciaTopTrimMaterial,
      ),
    ];
  };

  return buildTrimRails<FasciaTrimRail>({
    roofRailsTable,
    filter: fasciaTopTrimRailFilter,
    getKey: fasciaTopTrimGetKey,
    constructor: fasciaTopTrimRailConverter,
  });
}

interface ReprojectFriezeBoardRailOptions {
  readonly coplanarRails: CoplanarRails[];
  readonly subRoofs: Table<GeneratedRoof>;
  readonly walls: Table<WallSystem>;
  readonly offsetZ?: number;
  readonly extraWallLikeFaces?: Face[];
  readonly searchBoxTolerance?: number;
  readonly joinRailTolerance?: number;
}

export function reprojectFriezeBoardRail({
  coplanarRails,
  subRoofs,
  walls,
  extraWallLikeFaces = [],
  offsetZ = 0,
  searchBoxTolerance = MULTIGABLE_BSP_EPSILON,
  joinRailTolerance = MULTIGABLE_BSP_EPSILON,
}: ReprojectFriezeBoardRailOptions): CoplanarRails[] {
  const reprojectedFriezeBoardRails = reprojectCoplanarRailsArrayToWalls({
    coplanarRailsArray: coplanarRails,
    walls,
    subRoofs,
    extraWallLikeFaces,
    offsetZ,
  });

  // group the rails that share the same plane
  const coplanarFriezeBoardRails = groupCoplanarRails(reprojectedFriezeBoardRails);

  // join rails from the same group and create a rtree with the resulting rail ends
  return rejoinCoplanarRails({
    inputRails: coplanarFriezeBoardRails,
    keepRailsDirection: true,
    searchBoxTolerance,
    joinRailTolerance,
  });
}

function buildExtraWallLikeFace(soffitRail: SoffitRail, baseFriezePlane: Plane, isEnd: boolean): Face | null {
  const {
    innerPlane,
    midPlane,
    outerPlane,
    fasciaTopRail,
    outerTopRail,
    outerBottomRail,
    midBottomRail,
    innerTopRail,
    innerBottomRail,
  } = soffitRail;

  if (!(fasciaTopRail.length === outerBottomRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === outerTopRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === innerBottomRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === innerTopRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === midBottomRail.length)) {
    console.assert(false);
  }

  const rails3: PolyLine3[] = [
    polyLine2OnPlane(outerPlane, outerBottomRail),
    polyLine2OnPlane(outerPlane, outerTopRail),
    polyLine2OnPlane(innerPlane, innerTopRail),
    polyLine2OnPlane(innerPlane, innerBottomRail),
  ];

  const hasValidMidRail = !planeIsCoplanar(midPlane, outerPlane);
  if (hasValidMidRail) {
    rails3.push(polyLine2OnPlane(midPlane, midBottomRail));
  }

  const capPoints: number[] = [];
  for (let i = 0, l = rails3.length; i < l; i++) {
    const rail3 = rails3[i];

    const [x, y, z] = isEnd ? polyLine3EndPoint(rail3) : polyLine3StartPoint(rail3);
    const [px, py, pz] = planeProject3(baseFriezePlane, x, y, z);

    capPoints.push(px, py, pz);
  }

  const face = faceByNPoints(capPoints);

  if (!face) {
    return null;
  }

  if (isEnd) {
    faceOrNullReverse(face);
  }

  return face;
}

function getPorchBeamOutsideFaces(porches: Table<Porch>) {
  const outsideFaces: Face[] = [];

  function filterForOnlyHeaders<T extends ExteriorMaterialOverrideWithKeyMeasures>(s: T) {
    return s.porchKeyMeasuresGeometryType === PorchKeyMeasuresGeometryType.HEADER;
  }

  for (const porchId in porches) {
    const porch = porches[porchId];
    const { group } = porch;
    const filteredGroupFaces = group.toFaces(filterForOnlyHeaders);
    outsideFaces.push(...filteredGroupFaces);
  }
  return outsideFaces.map((face) => faceOrNullCopy(face));
}

function getLegacyFriezeBoardExtension(
  roofPerimeterRail: RoofPerimeterRail,
  subRoofs: Table<GeneratedRoof>,
  rtree: RBush3D,
  depth: number = 0,
): FriezeBoard[] {
  const { type, subRoofId, baseEdge, friezeBoard } = roofPerimeterRail;
  const subRoof = subRoofs[subRoofId];
  const { friezeBoardOrientation = StreetOrientationMode.FRONT_AND_SIDE_FACES } = subRoof.roofSettings;
  const hasCorrectOrientation = doesEdgeAlignsWithInputOrientation(baseEdge, friezeBoardOrientation);

  // early return
  if (
    !hasCorrectOrientation ||
    !subRoof.legacyRoof ||
    hasValidFriezeBoards(friezeBoard) ||
    type !== RoofPerimeterRailType.EAVE ||
    depth > MAX_LEGACY_FRIEZE_BOARD_EXTENSION_DEPTH
  ) {
    return [];
  }

  // detect neighbor rails
  const { type: startCornerType, otherPerimeterRail: startPerimeterRail } = getRoofPerimeterCornerInfo(
    roofPerimeterRail,
    rtree,
    false,
    false,
  );
  const { type: endCornerType, otherPerimeterRail: endPerimeterRail } = getRoofPerimeterCornerInfo(
    roofPerimeterRail,
    rtree,
    true,
    false,
  );

  // if we have start rail
  if (startPerimeterRail) {
    const startSubRoof = subRoofs[startPerimeterRail.subRoofId];
    const isGableEnd = startPerimeterRail.isStartEdge || startPerimeterRail.isEndEdge;

    // if it's another roof
    if (startPerimeterRail.subRoofId !== subRoofId && startSubRoof.index < subRoof.index) {
      // if it's a rake, we get the frieze board from it
      if (isGableEnd) {
        return startPerimeterRail.friezeBoard ?? [];
        // if it's a coplanar eave, we apply the same logic recursively
      } else if (startCornerType === RoofRailCornerType.COPLANAR) {
        return getLegacyFriezeBoardExtension(startPerimeterRail, subRoofs, rtree, depth + 1);
      }
      // if it is not and end side of the same roof, we apply the same logic recursively
    } else if (!isGableEnd || startPerimeterRail.subRoofId !== subRoofId) {
      return getLegacyFriezeBoardExtension(startPerimeterRail, subRoofs, rtree, depth + 1);
    }
  }

  // if we have end rail
  if (endPerimeterRail) {
    const endSubRoof = subRoofs[endPerimeterRail.subRoofId];
    const isGableEnd = endPerimeterRail.isStartEdge || endPerimeterRail.isEndEdge;

    // if it's another roof
    if (endPerimeterRail.subRoofId !== subRoofId && endSubRoof.index < subRoof.index) {
      // and it's a gable end, we get the frieze board from it
      if (isGableEnd) {
        return endPerimeterRail.friezeBoard ?? [];
        // if it's a coplanar eave, we apply the same logic recursively
      } else if (endCornerType === RoofRailCornerType.COPLANAR) {
        return getLegacyFriezeBoardExtension(endPerimeterRail, subRoofs, rtree, depth + 1);
      }
      // if it is not and end side of the same roof, we apply the same logic recursively
    } else if (!isGableEnd || endPerimeterRail.subRoofId !== subRoofId) {
      return getLegacyFriezeBoardExtension(endPerimeterRail, subRoofs, rtree, depth + 1);
    }
  }

  return [];
}

interface BuildFriezeTrimRailOptions {
  readonly subRoofs: Table<GeneratedRoof>;
  readonly walls: Table<WallSystem>;
  readonly roofRailsTable: Table<RoofPerimeterRail[]>;
  readonly clipContext: MultiGableRoofClipContext;
  readonly profileSource: ActiveProfileSource;
  readonly porches: Table<Porch>;
  readonly rtree: RBush3D;
  readonly wallOverlapForFriezeBoardBSPs: NodeBSP[];
  readonly gableWallOverlapBSPs: NodeBSP[];
  readonly halfStoryBSPsTable: Table<NodeBSP[]>;
  readonly gableReturnsRTreeMap: Table<RBush<AABB2>>;
  readonly overlapBSP: NodeBSP | null;
  readonly issues?: BuildingGenerationIssue[];
}

export function buildFriezeBoardRails({
  subRoofs,
  walls,
  roofRailsTable,
  clipContext,
  profileSource,
  porches,
  rtree,
  wallOverlapForFriezeBoardBSPs,
  gableWallOverlapBSPs,
  halfStoryBSPsTable,
  gableReturnsRTreeMap,
  overlapBSP,
  issues,
}: BuildFriezeTrimRailOptions): BuildTrimRailsResult<FriezeBoardRail> {
  const friezeBoardRailFilter = (roofPerimeterRail: RoofPerimeterRail): boolean => {
    // legacy behavior
    const extensionFriezeBoard = getLegacyFriezeBoardExtension(roofPerimeterRail, subRoofs, rtree);
    if (hasValidFriezeBoards(extensionFriezeBoard)) {
      return true;
    }
    const { friezeBoard } = roofPerimeterRail;

    return hasValidFriezeBoards(friezeBoard);
  };

  const friezeBoardGetKey = ({ friezeBoard }: RoofPerimeterRail): string => {
    if (!hasValidFriezeBoards(friezeBoard)) {
      return "";
    }

    assertIsArray(friezeBoard);

    const joinedFriezeBoardIds = friezeBoard.map((x) => x.friezeBoard).join("_");
    return joinedFriezeBoardIds;
  };

  const compareEndPoints = (rangeStart: number, rangeEnd: number, x: number): boolean => {
    return x >= rangeStart - END_POINT_TOLERANCE && x <= rangeEnd + END_POINT_TOLERANCE;
  };

  const hasReturnAtRail = (
    gableReturnsRTree: RBush<AABB2>,
    rail: PolyLine3,
    isEnd: boolean,
    halfBoxSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
  ): boolean => {
    const [x, y] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

    const aabb = aabb2ByTwoPoints(x - halfBoxSize, y - halfBoxSize, x + halfBoxSize, y + halfBoxSize);

    return gableReturnsRTree.search(aabb).length > 0;
  };

  const friezeBoardRailConstructor = (roofPerimeterRail: RoofPerimeterRail, groupId: string): FriezeBoardRail[] => {
    const {
      type,
      eaveType,
      eaveCutType,
      isDormer,
      rail,
      baseEdge,
      edgePlane,
      exteriorWallOffsetWithSheathing,
      exteriorWallOffsetFromOverhangDatum,
      roofEdgeStartOverhang,
      roofEdgeEndOverhang,
      appliedEndExtension,
      appliedStartExtension,
      rakeFriezeBoardExtents,
      overhang,
      prevPerimeterRail,
      nextPerimeterRail,
      subRoofId,
      friezeBoardMaterialLegacy,
    } = roofPerimeterRail;
    const { overhang: prevOverhang } = prevPerimeterRail ?? { overhang: 0 };
    const { overhang: nextOverhang } = nextPerimeterRail ?? { overhang: 0 };

    let { friezeBoard } = roofPerimeterRail;
    const subRoof = subRoofs[subRoofId];
    const gableReturnsRTree = gableReturnsRTreeMap[subRoofId] ?? new RBush<AABB2>();

    // legacy behavior
    const extensionFriezeBoard = getLegacyFriezeBoardExtension(roofPerimeterRail, subRoofs, rtree);
    if (hasValidFriezeBoards(extensionFriezeBoard)) {
      friezeBoard = extensionFriezeBoard;
    }

    const { insideBSP } = clipContext;
    const { roofSettings, levelId } = subRoof;
    const {
      eaveExtensionAtCoplanarRake = DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE,
      eaveFriezeBoardExtents = EaveFriezeBoardMode.EAVE_AND_EAVE_RETURNS,
      eaveFriezeBoard,
    } = roofSettings;

    const eaveReturnFriezeBoardTotalDepth: number | undefined =
      eaveFriezeBoardExtents === EaveFriezeBoardMode.EAVE_AND_EAVE_RETURNS
        ? friezeBoardsTotalDepth(profileSource, eaveFriezeBoard, issues, { type: "ROOF", id: subRoofId })
        : undefined;

    if (!rail) {
      return [];
    }

    const porchHeaderFaces = getPorchBeamOutsideFaces(porches);

    const groundPlane = planeNewXY(0, 0, 0);
    const isConnectedAtStart = isRoofPerimeterRailConnectedAtStart(roofPerimeterRail, rtree);
    const isConnectedAtEnd = isRoofPerimeterRailConnectedAtEnd(roofPerimeterRail, rtree);

    const signedStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
    const signedEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

    const absStartSlope = Math.abs(signedStartSlope);
    const absEndSlope = Math.abs(signedEndSlope);

    const hasStartExtension = appliedStartExtension > DIST_EPSILON;
    const hasEndExtension = appliedEndExtension > DIST_EPSILON;

    const baseFriezePlane = planeMoveAlongNormal(planeCopy(edgePlane), -(overhang - exteriorWallOffsetWithSheathing));
    const overlappingPorchHeaderAndFriezePlaneFaces = porchHeaderFaces
      .filter(
        (face) =>
          face &&
          planeIsParallel(face.plane, baseFriezePlane, FRIEZE_PORCH_HEADER_ANGLE_TOLERANCE) &&
          Math.abs(planeDistanceTo(baseFriezePlane, face.plane.originX, face.plane.originY, face.plane.originZ)) <
            FRIEZE_PORCH_HEADER_TOLERANCE,
      )
      .map((face) => {
        assertIsNotNullOrUndefined(face);

        const isCodirectional = planeIsCodirectional(face.plane, baseFriezePlane, FRIEZE_PORCH_HEADER_ANGLE_TOLERANCE);
        const alignedFaceWithFriezePlane = isCodirectional
          ? faceOrNullCopy(face)
          : faceOrNullReverse(faceOrNullCopy(face));

        assertIsNotNullOrUndefined(alignedFaceWithFriezePlane);

        const loopsTransformed = alignedFaceWithFriezePlane.loops.map(
          (x) => planeReProjectPolyLine2(x, alignedFaceWithFriezePlane.plane, baseFriezePlane),
          0,
        );
        return faceNew(loopsTransformed, baseFriezePlane);
      });

    const fullRailStartWrapMode = getFriezeBoardWrapMode(roofPerimeterRail, false);
    const fullRailEndWrapMode = getFriezeBoardWrapMode(roofPerimeterRail, true);

    let soffitRail = buildEnclosingRail(roofPerimeterRail, true);

    if (!isRoofTrimRailTypeSoffit(soffitRail)) {
      return [];
    }

    // if we have a squared eave, but we are at proper rake, we need to build a plumb soffit to avoid some frieze
    // board gaps when the eave overhang is so small that the eave soffit is inside the wall
    const { vX: srcSoffitVX, vY: srcSoffitVY, vZ: srcSoffitVZ } = soffitRail.outerPlane;
    if (
      eaveCutType === EaveCutType.SQUARED &&
      vec3Dot(srcSoffitVX, srcSoffitVY, srcSoffitVZ, 0, 0, 1) > ONE_MINUS_DIST_EPSILON
    ) {
      soffitRail = buildEnclosingRail(
        {
          ...roofPerimeterRail,
          eaveCutType: EaveCutType.PLUMB,
        },
        true,
      );

      if (!isRoofTrimRailTypeSoffit(soffitRail)) {
        return [];
      }
    }

    const { innerPlane, innerBottomRail, innerTopRail } = soffitRail;

    const prevExteriorWallOffsetWithFinish = prevPerimeterRail?.exteriorWallOffsetWithFinish ?? 0;
    const nextExteriorWallOffsetWithFinish = nextPerimeterRail?.exteriorWallOffsetWithFinish ?? 0;

    const prevExteriorWallOffsetFromOverhangDatum = prevPerimeterRail?.exteriorWallOffsetFromOverhangDatum ?? 0;
    const nextExteriorWallOffsetFromOverhangDatum = nextPerimeterRail?.exteriorWallOffsetFromOverhangDatum ?? 0;

    const clipFriezeNodes: ClippingNode[] = [];

    // this is needed because dormers are still not generating real walls
    // TODO: remove this when dormers are generating real walls
    if (isDormer) {
      // build start/end clip planes for frieze rails
      const clipFriezePlanes: PlaneBSP[] = [];
      if (!hasStartExtension && prevPerimeterRail?.type === RoofPerimeterRailType.RAKE && !isConnectedAtStart) {
        clipFriezePlanes.push(getEdgeEndPlaneBSP(baseEdge, prevExteriorWallOffsetWithFinish, false));
      }

      if (!hasEndExtension && nextPerimeterRail?.type === RoofPerimeterRailType.RAKE && !isConnectedAtEnd) {
        clipFriezePlanes.push(getEdgeEndPlaneBSP(baseEdge, nextExteriorWallOffsetWithFinish, true));
      }

      const clipFriezeBSP = addConvexPlanesToBSPLeaf(clipFriezePlanes);
      if (clipFriezeBSP) {
        clipFriezeNodes.push(clippingNodeNew(clipFriezeBSP, true));
      }

      clipFriezeNodes.push(clippingNodeNew(insideBSP, true));
    }

    const clipFriezeOptions: ClipRailContext = {
      clippingNodes: clipFriezeNodes,
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    const soffitInnerBottomRail = polyLine2OnPlane(innerPlane, innerBottomRail);
    const soffitInnerTopRail = polyLine2OnPlane(innerPlane, innerTopRail);
    const friezeSrcRail = polyLine3Copy(soffitInnerBottomRail);
    let friezeRail = polyLine3Copy(friezeSrcRail);
    // flatten the frieze board rail if the slopes are too small
    if (absStartSlope < FRIEZE_BOARD_SLOPE_EPSILON && absEndSlope < FRIEZE_BOARD_SLOPE_EPSILON) {
      friezeRail = flattenRailToMinZ(friezeRail);
    }

    // extensions
    const { type: startCornerType, otherPerimeterRail: prevCornerPerimeterRail } = getRoofPerimeterCornerInfo(
      roofPerimeterRail,
      rtree,
      false,
      false,
    );

    const { type: endCornerType, otherPerimeterRail: nextCornerPerimeterRail } = getRoofPerimeterCornerInfo(
      roofPerimeterRail,
      rtree,
      true,
      false,
    );

    const prevSoffit = prevCornerPerimeterRail
      ? buildEnclosingRail(prevCornerPerimeterRail, true)
      : roofTrimRailVoidNew();
    const nextSoffit = nextCornerPerimeterRail
      ? buildEnclosingRail(nextCornerPerimeterRail, true)
      : roofTrimRailVoidNew();

    if (prevCornerPerimeterRail) {
      const horizontalExtension =
        prevCornerPerimeterRail.overhang - prevCornerPerimeterRail.exteriorWallOffsetWithFinish;
      const startExtension = horizontalExtension / Math.cos(Math.atan(absStartSlope));
      const isCornerTypeExtendible = startCornerType === RoofRailCornerType.INSIDE;

      if (Math.abs(startExtension) > DIST_EPSILON && isCornerTypeExtendible) {
        polyLine3ExtendStart(friezeRail, startExtension);
      }
    }

    if (nextCornerPerimeterRail) {
      const horizontalExtension =
        nextCornerPerimeterRail.overhang - nextCornerPerimeterRail.exteriorWallOffsetWithFinish;
      const endExtension = horizontalExtension / Math.cos(Math.atan(absEndSlope));
      const isCornerTypeExtendible = endCornerType === RoofRailCornerType.INSIDE;

      if (Math.abs(endExtension) > DIST_EPSILON && isCornerTypeExtendible) {
        polyLine3ExtendEnd(friezeRail, endExtension);
      }
    }

    // add extra splits
    const hasValidStartSlopeForSplits =
      startCornerType === RoofRailCornerType.OUTSIDE
        ? signedStartSlope < -FRIEZE_BOARD_SLOPE_EPSILON
        : absStartSlope > FRIEZE_BOARD_SLOPE_EPSILON;
    const hasValidEndSlopeForSplits =
      endCornerType === RoofRailCornerType.OUTSIDE
        ? signedEndSlope < -FRIEZE_BOARD_SLOPE_EPSILON
        : absEndSlope > FRIEZE_BOARD_SLOPE_EPSILON;

    if (
      isRoofTrimRailTypeSoffit(prevSoffit) &&
      hasValidStartSlopeForSplits &&
      fullRailStartWrapMode === WrapFriezeBoardMode.NONE &&
      startCornerType !== RoofRailCornerType.COPLANAR
    ) {
      const minHeight = getSoffitInnerBottomHeight(prevSoffit, true);
      const minHeightPlane = planeBSPByPointAndNormal(0, 0, minHeight, 0, 0, 1);

      const int = intersectionLinePlaneBSP(
        minHeightPlane,
        friezeRail[0],
        friezeRail[1],
        friezeRail[2],
        friezeRail[3],
        friezeRail[4],
        friezeRail[5],
      );

      if (int) {
        const [tx, ty, tz] = polyLine3EdgeTangent(friezeRail, 0);
        const [newStartX, newStartY] = polyLine3StartPoint(friezeRail);
        const [nexTX, newTY, newTZ] = vec3Sub(int.x, int.y, int.z, newStartX, newStartY, minHeight);

        if (vec3Dot(tx, ty, tz, nexTX, newTY, newTZ) > DIST_EPSILON) {
          friezeRail[0] = int.x;
          friezeRail[1] = int.y;
          friezeRail[2] = int.z;

          friezeRail = polyLine3Simplify(polyLine3Concat([newStartX, newStartY, minHeight], friezeRail));
        }
      }
    }

    if (
      isRoofTrimRailTypeSoffit(nextSoffit) &&
      hasValidEndSlopeForSplits &&
      fullRailEndWrapMode === WrapFriezeBoardMode.NONE &&
      endCornerType !== RoofRailCornerType.COPLANAR
    ) {
      const minHeight = getSoffitInnerBottomHeight(nextSoffit, false);
      const minHeightPlane = planeBSPByPointAndNormal(0, 0, minHeight, 0, 0, 1);

      const int = intersectionLinePlaneBSP(
        minHeightPlane,
        friezeRail[friezeRail.length - 6],
        friezeRail[friezeRail.length - 5],
        friezeRail[friezeRail.length - 4],
        friezeRail[friezeRail.length - 3],
        friezeRail[friezeRail.length - 2],
        friezeRail[friezeRail.length - 1],
      );

      if (int) {
        const [tx, ty, tz] = polyLine3EdgeTangent(friezeRail, polyLine3EdgeCount(friezeRail) - 1);
        const [newEndX, newEndY] = polyLine3EndPoint(friezeRail);
        const [nexTX, newTY, newTZ] = vec3Sub(newEndX, newEndY, minHeight, int.x, int.y, int.z);

        if (vec3Dot(tx, ty, tz, nexTX, newTY, newTZ) > DIST_EPSILON) {
          friezeRail[friezeRail.length - 3] = int.x;
          friezeRail[friezeRail.length - 2] = int.y;
          friezeRail[friezeRail.length - 1] = int.z;

          friezeRail = polyLine3Simplify(polyLine3Concat(friezeRail, [newEndX, newEndY, minHeight]));
        }
      }
    }

    //apply extension correction when the eave is boxed open and refHeelHeightOffset is negative
    if (
      isBoxEave[eaveType] &&
      type === RoofPerimeterRailType.RAKE &&
      (absStartSlope > FRIEZE_BOARD_SLOPE_EPSILON || absEndSlope > FRIEZE_BOARD_SLOPE_EPSILON)
    ) {
      const {
        roofVolume: { heelHeightOffsets },
      } = subRoofs[subRoofId];
      if (
        heelHeightOffsets.refOffset < -DIST_EPSILON &&
        prevCornerPerimeterRail &&
        startCornerType === RoofRailCornerType.OUTSIDE
      ) {
        const horizontalExtension =
          prevCornerPerimeterRail.overhang - 2 * prevCornerPerimeterRail.exteriorWallOffsetWithFinish;
        const startExtension = horizontalExtension / Math.cos(Math.atan(absStartSlope));

        polyLine3ExtendStart(friezeRail, -startExtension);
      }
      if (
        heelHeightOffsets.refOffset < -DIST_EPSILON &&
        nextCornerPerimeterRail &&
        endCornerType === RoofRailCornerType.OUTSIDE
      ) {
        const horizontalExtension =
          nextCornerPerimeterRail.overhang - 2 * nextCornerPerimeterRail.exteriorWallOffsetWithFinish;
        const endExtension = horizontalExtension / Math.cos(Math.atan(absEndSlope));
        polyLine3ExtendEnd(friezeRail, -endExtension);
      }
    }

    const clippedFriezeRails = clipRail3({
      rail: friezeRail,
      options: clipFriezeOptions,
    });

    let [projDirectionX, projDirectionY, projDirectionZ] = planeNormal(baseFriezePlane);

    // for angled eaves, we need to use the slope direction as the projection direction
    if (isAngledEave[eaveType]) {
      [projDirectionX, projDirectionY, projDirectionZ] = getRoofPerimeterRailStartRafterDir(roofPerimeterRail);
    }

    let coplanarFriezeBoardRails = [
      coplanarRailsNew(baseFriezePlane, projDirectionX, projDirectionY, projDirectionZ, clippedFriezeRails),
    ];

    if (!isDormer) {
      // reprojection using a small negative offset for the rails to avoid generating floating
      // frieze boards on top of porch beams
      coplanarFriezeBoardRails = reprojectFriezeBoardRail({
        coplanarRails: coplanarFriezeBoardRails,
        subRoofs,
        walls,
        offsetZ: -MULTIGABLE_BSP_EPSILON,
        extraWallLikeFaces: [...overlappingPorchHeaderAndFriezePlaneFaces],
        searchBoxTolerance: FRIEZE_RAIL_SEARCH_BOX_TOLERANCE,
      });
    }

    const hasStartReturn = hasReturnAtRail(gableReturnsRTree, rail, false);
    const hasEndReturn = hasReturnAtRail(gableReturnsRTree, rail, true);
    const halfStoriesBSPs = halfStoryBSPsTable[levelId];
    const wallOverlapBSPs = [...wallOverlapForFriezeBoardBSPs, ...gableWallOverlapBSPs, ...halfStoriesBSPs];

    const overhangAndFriezeBasedClipPlaneOrigin = (
      rail: PolyLine3,
      normal: number[],
      isEnd: boolean,
      friezeBoardSize: number,
    ): number[] => {
      const edge = !isEnd ? polyLine3Edge(rail, 0) : polyLine3Edge(rail, polyLine3EdgeCount(rail) - 1);
      const [originX, originY, originZ] = !isEnd ? polyLine3StartPoint(edge) : polyLine3EndPoint(edge);

      if (type === RoofPerimeterRailType.RAKE && !isDormer && wallOverlapBSPs.length > 0) {
        let friezeBoardCornerOffsetInsideAWall = false;
        const overhang = !isEnd
          ? prevOverhang - prevExteriorWallOffsetFromOverhangDatum
          : nextOverhang - nextExteriorWallOffsetFromOverhangDatum;
        let overhangSloped = !isEnd
          ? prevOverhang / Math.cos(Math.atan(absStartSlope))
          : nextOverhang / Math.cos(Math.atan(absEndSlope));
        const friezeBoardSizeHorizontal = !isEnd
          ? friezeBoardSize / Math.cos(Math.atan(absStartSlope))
          : friezeBoardSize / Math.cos(Math.atan(absEndSlope));
        if (overhang > friezeBoardSizeHorizontal) {
          overhangSloped = friezeBoardSizeHorizontal;
        }
        const [tx, ty, tz] = !isEnd
          ? polyLine3EdgeTangent(rail, 0)
          : polyLine3EdgeTangent(rail, polyLine3EdgeCount(rail) - 1);
        const [ux, uy, uz] = vec3CrossNormalize(tx, ty, tz, normal[0], normal[1], normal[2]);
        const [px, py, pz] = vec3Add(
          originX,
          originY,
          originZ,
          ux * overhangSloped,
          uy * overhangSloped,
          uz * overhangSloped,
        );
        const [pxInsideWall, pyInsideWall, pzInsideWall] = vec3Add(
          px,
          py,
          pz,
          -normal[0] * exteriorWallOffsetWithSheathing,
          -normal[1] * exteriorWallOffsetWithSheathing,
          -normal[2] * exteriorWallOffsetWithSheathing,
        );

        for (const bsp of wallOverlapBSPs) {
          if (isPointInsideBSP(bsp, pxInsideWall, pyInsideWall, pzInsideWall, true, MULTIGABLE_BSP_EPSILON)) {
            friezeBoardCornerOffsetInsideAWall = true;
            break;
          }
        }

        if (!friezeBoardCornerOffsetInsideAWall) {
          const [originXClipPt, originYClipPt] = !isEnd ? polyLine3EndPoint(edge) : polyLine3StartPoint(edge);
          return [originXClipPt, originYClipPt];
        }
      }
      return [originX, originY];
    };

    const friezeRails: FriezeBoardRail[] = [];
    for (const { rails, plane } of coplanarFriezeBoardRails) {
      const refEdge = planeReProjectPolyLine2(
        polyLine2Extend(polyLine2Copy(baseEdge), prevExteriorWallOffsetWithFinish, nextExteriorWallOffsetWithFinish),
        groundPlane,
        plane,
      );

      const [nX, nY, nZ] = planeNormal(plane);

      const [startX1] = polyLine2Start(refEdge);
      const [endX1] = polyLine2End(refEdge);

      const friezeSrcRail2D = planeProjectPolyLine3(plane, friezeSrcRail);
      const [startX2] = polyLine2Start(friezeSrcRail2D);
      const [endX2] = polyLine2End(friezeSrcRail2D);

      assertIsArray(friezeBoard);

      for (const {
        lateralOffset = 0,
        verticalOffset = 0,
        friezeBoard: friezeBoardProfileId,
        friezeBoardMaterial,
      } of friezeBoard) {
        const friezeBoardSize = getFriezeBoardSize(friezeBoardProfileId, profileSource, issues, {
          type: "ROOF",
          id: subRoofId,
        });

        for (const rail of rails) {
          const railCopy = polyLine3Copy(rail);
          const projectRailCopy = planeProjectPolyLine3(plane, railCopy);
          const offsetProjectRailCopy = offsetPolyLine2(projectRailCopy, verticalOffset);
          const interimOffsettedRail = polyLine2OnPlane(plane, offsetProjectRailCopy);
          alignBeginAndEndPointsOfOffsettedRail(rail, interimOffsettedRail);
          const finalOffsetRail = polyLine3Move(
            interimOffsettedRail,
            nX * lateralOffset,
            nY * lateralOffset,
            nZ * lateralOffset,
          );
          const transformedFriezePlane = planeMoveAlongNormal(planeCopy(plane), lateralOffset);
          const rail2D = planeProjectPolyLine3(transformedFriezePlane, finalOffsetRail);
          const [myStartX] = polyLine2Start(rail2D);
          const [myEndX] = polyLine2End(rail2D);

          const isStart = compareEndPoints(startX2, startX1, myStartX);
          const isEnd = compareEndPoints(endX1, endX2, myEndX);

          const startWrapMode = isStart && hasStartReturn ? fullRailStartWrapMode : WrapFriezeBoardMode.NONE;
          const endWrapMode = isEnd && hasEndReturn ? fullRailEndWrapMode : WrapFriezeBoardMode.NONE;

          // special case for boxed open rakes (for extending the frieze board to cover the soffit)
          let startClipPlanes: FriezePlaneGroup | undefined;
          let endClipPlanes: FriezePlaneGroup | undefined;
          const extraWallLikeFaces: Face[] = [...overlappingPorchHeaderAndFriezePlaneFaces];
          if (
            eaveType === EaveType.BOXED_OPEN &&
            type === RoofPerimeterRailType.RAKE &&
            rakeFriezeBoardExtents === RakeFriezeBoardMode.WALL_AND_SOFFIT
          ) {
            if (isStart && isRoofTrimRailTypeSoffit(prevSoffit)) {
              const extraWallLikeFace = buildExtraWallLikeFace(prevSoffit, baseFriezePlane, true);
              if (extraWallLikeFace) {
                extraWallLikeFaces.push(extraWallLikeFace);
              }

              const minHeight = getSoffitInnerBottomHeight(prevSoffit, true);
              const { dist, clipPlane } = getBoxedOpenFriezeBoardExtension({
                friezeRail: finalOffsetRail,
                soffitInnerTopRail,
                soffitInnerBottomRail,
                minHeight,
                isEnd: false,
              });

              if (dist > DIST_EPSILON) {
                const [originX, originY] = overhangAndFriezeBasedClipPlaneOrigin(
                  finalOffsetRail,
                  [nX, nY, nZ],
                  false,
                  friezeBoardSize,
                );

                if (prevCornerPerimeterRail?.type !== RoofPerimeterRailType.RAKE) {
                  //makes it a rake-rake corner, so don't extend
                  polyLine3ExtendStart(finalOffsetRail, dist);
                }

                startClipPlanes = friezePlaneGroupNew(planeNew(originX, originY, 0, nX, nY, nZ, 0, 0, 1), clipPlane);
              }
            }

            if (isEnd && isRoofTrimRailTypeSoffit(nextSoffit)) {
              const extraWallLikeFace = buildExtraWallLikeFace(nextSoffit, baseFriezePlane, false);
              if (extraWallLikeFace) {
                extraWallLikeFaces.push(extraWallLikeFace);
              }

              const minHeight = getSoffitInnerBottomHeight(nextSoffit, false);
              const { dist, clipPlane } = getBoxedOpenFriezeBoardExtension({
                friezeRail: finalOffsetRail,
                soffitInnerTopRail,
                soffitInnerBottomRail,
                minHeight,
                isEnd: true,
              });

              if (dist > DIST_EPSILON) {
                const [originX, originY] = overhangAndFriezeBasedClipPlaneOrigin(
                  finalOffsetRail,
                  [nX, nY, nZ],
                  true,
                  friezeBoardSize,
                );

                if (nextCornerPerimeterRail?.type !== RoofPerimeterRailType.RAKE) {
                  //makes it a rake-rake corner, so don't extend
                  polyLine3ExtendEnd(finalOffsetRail, dist);
                }

                endClipPlanes = friezePlaneGroupNew(planeNew(originX, originY, 0, -nX, -nY, -nZ, 0, 0, 1), clipPlane);
              }
            }
          }
          friezeRails.push(
            roofFriezeBoardRailNew(
              finalOffsetRail,
              roofPerimeterRail,
              transformedFriezePlane,
              friezeBoardProfileId,
              startWrapMode,
              endWrapMode,
              groupId,
              friezeBoardMaterial ?? friezeBoardMaterialLegacy,
              startClipPlanes,
              endClipPlanes,
              extraWallLikeFaces,
              verticalOffset,
              lateralOffset,
            ),
          );
        }
      }
    }

    const wrappedFriezeRails: FriezeBoardRail[] = [];
    for (const friezeRail of friezeRails) {
      const {
        rail: baseRail,
        friezePlane,
        roofPerimeterRail,
        startWrapMode,
        endWrapMode,
        startClipPlanes,
        endClipPlanes,
        extraWallLikeFaces,
        friezeBoard: friezeBoardProfileId,
        material: friezeBoardMaterial,
        friezeBoardVerticalOffset,
        friezeBoardLateralOffset,
      } = friezeRail;
      const { eaveReturnDistance, type, isEndEdge } = roofPerimeterRail;
      const baseRail2D = planeProjectPolyLine3(friezePlane, baseRail);
      const [myStartX, myStartY] = polyLine2Start(baseRail2D);
      const [myEndX, myEndY] = polyLine2End(baseRail2D);
      const friezeBoardSize = getFriezeBoardSize(friezeBoardProfileId, profileSource, issues, {
        type: "ROOF",
        id: subRoofId,
      });

      const refEdge = planeReProjectPolyLine2(
        polyLine2Extend(polyLine2Copy(baseEdge), prevExteriorWallOffsetWithFinish, nextExteriorWallOffsetWithFinish),
        groundPlane,
        friezePlane,
      );
      const [startX1] = polyLine2Start(refEdge);
      const [endX1] = polyLine2End(refEdge);

      const friezeSrcRail2D = planeProjectPolyLine3(friezePlane, friezeSrcRail);
      const [startX2] = polyLine2Start(friezeSrcRail2D);
      const [endX2] = polyLine2End(friezeSrcRail2D);

      const isCoplanarEaveAtStart: boolean = vec2DistSquared(startX2, myStartY, myStartX, myStartY) < 0.5;
      const isCoplanarEaveAtEnd: boolean = vec2DistSquared(endX2, myEndY, myEndX, myEndY) < 0.5;

      const hasStartCorner = roofHasCorner({
        subRoof,
        isEndSide: isEndEdge,
        isProfileStartSide: false,
        overlapBSP,
      });

      const hasEndCorner = roofHasCorner({
        subRoof,
        isEndSide: isEndEdge,
        isProfileStartSide: true,
        overlapBSP,
      });

      let friezeSubRails: PolyLine2[] = [];
      let hasStartExtension = false;
      let hasEndExtension = false;

      if (type === RoofPerimeterRailType.RAKE) {
        const distToStart1 = startX1 - myStartX;
        const distToStart2 = startX2 - myStartX;
        const distToEnd1 = myEndX - endX1;
        const distToEnd2 = myEndX - endX2;

        // snap to the closest end point
        const additionalOffsetAtStart = Math.abs(distToStart1) < Math.abs(distToStart2) ? distToStart1 : distToStart2;
        const additionalOffsetAtEnd = Math.abs(distToEnd1) < Math.abs(distToEnd2) ? distToEnd1 : distToEnd2;

        assertIsNumber(friezeBoardVerticalOffset);

        const refStartEaveReturnDistance = hasStartCorner ? eaveReturnDistance : eaveExtensionAtCoplanarRake;
        const refEndEaveReturnDistance = hasEndCorner ? eaveReturnDistance : eaveExtensionAtCoplanarRake;

        const startEaveReturnDistance =
          Math.max(
            DIST_EPSILON,
            isCoplanarEaveAtStart
              ? roofEdgeStartOverhang +
                  refStartEaveReturnDistance -
                  exteriorWallOffsetFromOverhangDatum +
                  additionalOffsetAtStart
              : eaveReturnDistance +
                  prevExteriorWallOffsetWithFinish -
                  prevExteriorWallOffsetFromOverhangDatum +
                  additionalOffsetAtStart,
          ) + friezeBoardVerticalOffset;

        const endEaveReturnDistance =
          Math.max(
            DIST_EPSILON,
            isCoplanarEaveAtEnd
              ? roofEdgeEndOverhang +
                  refEndEaveReturnDistance -
                  exteriorWallOffsetFromOverhangDatum +
                  additionalOffsetAtEnd
              : eaveReturnDistance +
                  nextExteriorWallOffsetWithFinish -
                  nextExteriorWallOffsetFromOverhangDatum +
                  additionalOffsetAtEnd,
          ) + friezeBoardVerticalOffset;

        const {
          friezeBoardRails: friezeSubRailsRake,
          hasEndExtension: hasEndExtensionRake,
          hasStartExtension: hasStartExtensionRake,
        } = buildFriezeBoardSubRails({
          baseRail,
          friezePlane,
          roofPerimeterRail,
          startWrapMode,
          endWrapMode,
          startEaveReturnDistance,
          endEaveReturnDistance,
          friezeBoardSize,
          isCoplanarEaveAtStart,
          isCoplanarEaveAtEnd,
          friezeBoardVerticalOffset,
          hasStartCorner,
          hasEndCorner,
          eaveReturnFriezeBoardTotalDepth,
        });
        friezeSubRails = friezeSubRailsRake;
        hasEndExtension = hasEndExtensionRake;
        hasStartExtension = hasStartExtensionRake;
      } else {
        const baseRailCopy = polyLine3Copy(baseRail);

        let hasNeighborStartReturn =
          isCoplanarEaveAtStart && prevPerimeterRail && !isOpenEave[prevPerimeterRail.eaveType];
        if (hasNeighborStartReturn) {
          assertIsNotNullOrUndefined(prevPerimeterRail);

          const neighborRoofReturnsRTree = gableReturnsRTreeMap[prevPerimeterRail.subRoofId];
          hasNeighborStartReturn =
            neighborRoofReturnsRTree && hasReturnAtRail(neighborRoofReturnsRTree, prevPerimeterRail.rail, true);

          hasNeighborStartReturn = hasNeighborStartReturn || prevPerimeterRail.type === RoofPerimeterRailType.EAVE;
        }

        let hasNeighborEndReturn = isCoplanarEaveAtEnd && nextPerimeterRail && !isOpenEave[nextPerimeterRail.eaveType];
        if (hasNeighborEndReturn) {
          assertIsNotNullOrUndefined(nextPerimeterRail);

          const neighborRoofReturnsRTree = gableReturnsRTreeMap[nextPerimeterRail.subRoofId];
          hasNeighborEndReturn =
            neighborRoofReturnsRTree && hasReturnAtRail(neighborRoofReturnsRTree, nextPerimeterRail.rail, false);

          hasNeighborEndReturn = hasNeighborEndReturn || nextPerimeterRail.type === RoofPerimeterRailType.EAVE;
        }

        if (hasNeighborStartReturn) {
          assertIsNotNullOrUndefined(prevPerimeterRail);

          const {
            subRoofId,
            exteriorWallOffsetFromOverhangDatum,
            roofEdgeEndOverhang,
            fasciaBoardSize,
            soffitOffset,
            isEndEdge,
            rail: prevRail,
            type: prevType,
          } = prevPerimeterRail;
          const startSlope = polyLine3EdgeSlopeInZ(rail, 0);
          const prevSlope = polyLine3EdgeSlopeInZ(prevRail, polyLine3EdgeCount(prevRail) - 1);
          const angle = Math.atan(startSlope);
          const prevAngle = Math.atan(prevSlope);
          const angleDiff = angle - prevAngle;
          const prevSubRoof = subRoofs[subRoofId];
          const { roofSettings } = prevSubRoof;
          const {
            eaveReturnDistance = DEFAULT_EAVE_RETURN_DISTANCE,
            eaveExtensionAtCoplanarRake = DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE,
          } = roofSettings;
          const soffitDepth = fasciaBoardSize - soffitOffset;
          const hasStartCorner = roofHasCorner({
            subRoof: prevSubRoof,
            isEndSide: isEndEdge,
            isProfileStartSide: true,
            overlapBSP,
          });
          const startEaveReturnDistance = hasStartCorner ? eaveReturnDistance : eaveExtensionAtCoplanarRake;
          const startOverhangExtension =
            prevType === RoofPerimeterRailType.RAKE
              ? startEaveReturnDistance +
                roofEdgeEndOverhang -
                exteriorWallOffsetFromOverhangDatum -
                soffitDepth * Math.tan(0.5 * angleDiff)
              : 0;
          if (Math.abs(startOverhangExtension) >= DIST_EPSILON) {
            polyLine3ExtendStart(baseRailCopy, startOverhangExtension);
            hasStartExtension = true;
          }
        }

        if (hasNeighborEndReturn) {
          assertIsNotNullOrUndefined(nextPerimeterRail);

          const {
            subRoofId,
            exteriorWallOffsetFromOverhangDatum,
            roofEdgeStartOverhang,
            fasciaBoardSize,
            soffitOffset,
            isEndEdge,
            rail: nextRail,
            type: nextType,
          } = nextPerimeterRail;
          const endSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);
          const nextSlope = -polyLine3EdgeSlopeInZ(nextRail, 0);
          const angle = Math.atan(endSlope);
          const nextAngle = Math.atan(nextSlope);
          const angleDiff = angle - nextAngle;
          const nextSubRoof = subRoofs[subRoofId];
          const { roofSettings } = nextSubRoof;
          const {
            eaveReturnDistance = DEFAULT_EAVE_RETURN_DISTANCE,
            eaveExtensionAtCoplanarRake = DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE,
          } = roofSettings;
          const soffitDepth = fasciaBoardSize - soffitOffset;
          const hasEndCorner = roofHasCorner({
            subRoof: nextSubRoof,
            isEndSide: isEndEdge,
            isProfileStartSide: false,
            overlapBSP,
          });
          const endEaveReturnDistance = hasEndCorner ? eaveReturnDistance : eaveExtensionAtCoplanarRake;
          const endOverhangExtension =
            nextType === RoofPerimeterRailType.RAKE
              ? endEaveReturnDistance +
                roofEdgeStartOverhang -
                exteriorWallOffsetFromOverhangDatum -
                soffitDepth * Math.tan(0.5 * angleDiff)
              : 0;
          if (Math.abs(endOverhangExtension) >= DIST_EPSILON) {
            polyLine3ExtendEnd(baseRailCopy, endOverhangExtension);
            hasEndExtension = true;
          }
        }
        friezeSubRails.push(baseRailCopy);
      }

      let coplanarFriezeBoardRails = [
        coplanarRailsNew(
          friezePlane,
          projDirectionX,
          projDirectionY,
          projDirectionZ,
          friezeSubRails,
          friezeBoardLateralOffset,
        ),
      ];

      if (!isDormer && (hasStartExtension || hasEndExtension)) {
        // reprojection using a small negative offset for the rails to avoid generating floating
        // frieze boards on top of porch beams
        coplanarFriezeBoardRails = reprojectFriezeBoardRail({
          coplanarRails: coplanarFriezeBoardRails,
          subRoofs,
          walls,
          extraWallLikeFaces,
          offsetZ: -MULTIGABLE_BSP_EPSILON,
          searchBoxTolerance: FRIEZE_RAIL_SEARCH_BOX_TOLERANCE,
        });
      }

      for (const { plane, rails } of coplanarFriezeBoardRails) {
        for (const subRail of rails) {
          const subRail2D = planeProjectPolyLine3(plane, subRail);
          const [myStartX] = polyLine2Start(subRail2D);
          const [myEndX] = polyLine2End(subRail2D);

          const isStart = compareEndPoints(startX2, startX1, myStartX);
          const isEnd = compareEndPoints(endX1, endX2, myEndX);

          const startWrapMode = isStart ? fullRailStartWrapMode : WrapFriezeBoardMode.NONE;
          const endWrapMode = isEnd ? fullRailEndWrapMode : WrapFriezeBoardMode.NONE;

          wrappedFriezeRails.push(
            roofFriezeBoardRailNew(
              subRail,
              roofPerimeterRail,
              plane,
              friezeBoardProfileId,
              startWrapMode,
              endWrapMode,
              groupId,
              friezeBoardMaterial ?? friezeBoardMaterialLegacy,
              startClipPlanes,
              endClipPlanes,
              extraWallLikeFaces,
            ),
          );
        }
      }
    }
    return wrappedFriezeRails;
  };

  const getFriezeBoardExtraRails = (subRoofId: string): FriezeBoardRail[] => {
    const subRoof = subRoofs[subRoofId];

    if (!subRoof) {
      return [];
    }

    const { roofSettings, isDormer } = subRoof;
    const {
      eaveType,
      eaveCutType = EaveCutType.PLUMB,
      friezeBoardOrientation = StreetOrientationMode.FRONT_AND_SIDE_FACES,
      rakeFriezeBoardExtents,
      eaveFriezeBoard,
      eaveFriezeBoardMaterialLegacy,
      eaveOverhang,
    } = roofSettings;

    if (!hasValidFriezeBoards(eaveFriezeBoard)) {
      return [];
    }

    const extraFriezeRails: FriezeBoardRail[] = [];

    const extraRails = buildExtraFriezeBoardRails({
      subRoof,
      walls,
      friezeBoardOrientation,
      clipContext,
    });

    for (const { plane, rails } of extraRails) {
      const [nX, nY, nZ] = planeNormal(plane);
      const { vX, vY, vZ } = plane;
      const coplanarExtraFriezeRails: FriezeBoardRail[] = [];
      for (const rail of rails) {
        if (!rail || polyLine3EdgeCount(rail) < 1) {
          continue;
        }
        const baseEdge = planeProjectPolyLine3(planeNewXY(), rail);
        const wallLimitsRail = polyLine2Copy(baseEdge);
        const edgeCount = polyLine3EdgeCount(rail);
        const normals = new Array<Vec3>(edgeCount).fill([vX, vY, vZ]);
        const rafterDirections = new Array<Vec3>(edgeCount).fill([nX, nY, nZ]);

        assertIsNotNullOrUndefined(eaveFriezeBoard);

        const roofPerimeterRail = roofPerimeterRailNew({
          type: RoofPerimeterRailType.EAVE,
          isDormer,
          subRoofId,
          side: RoofSide.INVALID,
          edgeIndex: -1,
          isStartEdge: false,
          isEndEdge: false,
          edgePlane: plane,
          baseEdge,
          wallLimitsRail,
          rail,
          baseRail: polyLine3Copy(rail),
          normals,
          baseNormals: [...normals],
          rafterDirections,
          baseRafterDirections: [...rafterDirections],
          eaveType,
          eaveCutType,
          eaveReturnDistance: 0,
          exteriorWallOffsetWithSheathing: 0,
          exteriorWallOffsetWithFinish: 0,
          exteriorWallOffsetFromOverhangDatum: 0,
          overhang: 0,
          roofEdgeStartOverhang: 0,
          roofEdgeEndOverhang: 0,
          includeExposedRafters: false,
          includeFasciaBoard: false,
          fasciaBoardSize: 0,
          adjustFasciaBoardSize: false,
          includeSoffit: false,
          soffitOffset: 0,
          rakeFriezeBoardExtents,
          friezeBoard: eaveFriezeBoard,
          friezeBoardMaterialLegacy: eaveFriezeBoardMaterialLegacy,
          wrapFriezeBoardModeAtStart: WrapFriezeBoardMode.NONE,
          wrapFriezeBoardModeAtEnd: WrapFriezeBoardMode.NONE,
          includeFasciaTopTrim: false,
          fasciaTopTrimSize: 0,
          fasciaBoardThickness: Math.min(eaveOverhang - COMPACTNESS_EPSILON, FASCIA_THICKNESS),
          eaveCutTypeAtStart: RoofRailEndCondition.EXTENDED,
          eaveCutTypeAtEnd: RoofRailEndCondition.EXTENDED,
        });

        for (const {
          lateralOffset = 0,
          verticalOffset = 0,
          friezeBoard: profile,
          friezeBoardMaterial,
        } of eaveFriezeBoard) {
          const railCopy = polyLine3Copy(rail);
          const projectRailCopy = planeProjectPolyLine3(plane, railCopy);
          const offsetProjectRailCopy = offsetPolyLine2(projectRailCopy, verticalOffset);
          const interimOffsettedRail = polyLine2OnPlane(plane, offsetProjectRailCopy);
          alignBeginAndEndPointsOfOffsettedRail(rail, interimOffsettedRail);
          const finalOffsetRail = polyLine3Move(
            interimOffsettedRail,
            nX * lateralOffset,
            nY * lateralOffset,
            nZ * lateralOffset,
          );
          const transformedFriezePlane = planeMoveAlongNormal(planeCopy(plane), lateralOffset);

          coplanarExtraFriezeRails.push(
            roofFriezeBoardRailNew(
              finalOffsetRail,
              roofPerimeterRail,
              transformedFriezePlane,
              profile,
              WrapFriezeBoardMode.NONE,
              WrapFriezeBoardMode.NONE,
              subRoofId,
              friezeBoardMaterial ?? eaveFriezeBoardMaterialLegacy,
            ),
          );
        }
      }
      extraFriezeRails.push(...coplanarExtraFriezeRails);
    }

    const rtree = new RBush3D();

    buildRoofTrimRailsRTree(extraFriezeRails, rtree);

    const extraFriezeRailsFiltered: FriezeBoardRail[] = [];

    for (const friezeRail of extraFriezeRails) {
      const connectedRailsStart = getConnectedRoofTrimRails({
        roofTrimRail: friezeRail,
        rtree,
        isEnd: false,
      });
      const connectedRailsEnd = getConnectedRoofTrimRails({
        roofTrimRail: friezeRail,
        rtree,
        isEnd: false,
      });
      if (connectedRailsStart.length === 0 && connectedRailsEnd.length === 0) {
        const { rail } = friezeRail;
        if (polyLine3Length(rail) < FRIEZE_BOARD_MIN_LENGTH) {
          continue;
        }
      }
      extraFriezeRailsFiltered.push(friezeRail);
    }

    return extraFriezeRailsFiltered;
  };

  return buildTrimRails<FriezeBoardRail>({
    roofRailsTable,
    filter: friezeBoardRailFilter,
    getKey: friezeBoardGetKey,
    constructor: friezeBoardRailConstructor,
    getExtraRails: getFriezeBoardExtraRails,
  });
}

interface ConnectedRoofTrimRailsOptions {
  readonly roofTrimRail: RoofTrimRail;
  readonly rtree: RBush3D;
  readonly isEnd: boolean;
  readonly filter?: ConnectedRoofTrimRailFilter;
  readonly boxHalfSize?: number;
}

function getConnectedRoofTrimRails({
  roofTrimRail,
  rtree,
  isEnd,
  filter,
  boxHalfSize = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
}: ConnectedRoofTrimRailsOptions): RoofTrimRail[] {
  const connectedRails: RoofTrimRail[] = [];

  if (isRoofTrimRailTypeVoid(roofTrimRail)) {
    return connectedRails;
  }

  const { rail, roofPerimeterRail } = roofTrimRail;
  const { subRoofId } = roofPerimeterRail;
  const [x, y, z] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

  // search for other connected trim rails in the rtree
  const bbox = aabb3ByTwoPoints(
    x - boxHalfSize,
    y - boxHalfSize,
    z - boxHalfSize,
    x + boxHalfSize,
    y + boxHalfSize,
    z + boxHalfSize,
  );

  const results = rtree.search(bbox);

  for (const result of results) {
    const { roofTrimRail: otherRoofRoofTrimRail, isEnd: isOtherEnd } = result as RoofTrimRailBox;

    if (isRoofTrimRailTypeVoid(otherRoofRoofTrimRail)) {
      continue;
    }

    const { roofPerimeterRail: otherRoofPerimeterRail } = otherRoofRoofTrimRail;

    // discard the same trim rail
    if (subRoofId === otherRoofPerimeterRail.subRoofId && isEnd === isOtherEnd) {
      continue;
    }

    // apply filter
    if (filter && !filter(roofTrimRail, otherRoofRoofTrimRail, isEnd, isOtherEnd)) {
      continue;
    }

    // add to connected rails
    connectedRails.push(otherRoofRoofTrimRail);
  }

  return connectedRails
    .filter(isNonVoidRoofTrimRailType)
    .sort((a, b) => polyLine3Length(b.rail) - polyLine3Length(a.rail));
}

function getIntersectingRoofTrimRails({
  roofTrimRail,
  rtree,
  isEnd,
  filter,
  boxHalfSize = ROOF_RAIL_RTREE_BOX_HALF_WIDTH_FOR_INTERSECTION,
}: ConnectedRoofTrimRailsOptions): RoofTrimRail[] {
  const connectedRails: RoofTrimRail[] = [];

  if (isRoofTrimRailTypeVoid(roofTrimRail)) {
    return connectedRails;
  }

  const { rail } = roofTrimRail;
  const [x, y, z] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

  // search for other connected trim rails in the rtree
  const bbox = aabb3ByTwoPoints(
    x - boxHalfSize,
    y - boxHalfSize,
    z - boxHalfSize,
    x + boxHalfSize,
    y + boxHalfSize,
    z + boxHalfSize,
  );

  const results = rtree.search(bbox);

  for (const result of results) {
    const { roofTrimRail: otherRoofRoofTrimRail, isEnd: isOtherEnd } = result as RoofTrimRailBox;

    if (isRoofTrimRailTypeVoid(otherRoofRoofTrimRail)) {
      continue;
    }

    // discard the same trim rail
    if (roofTrimRail === otherRoofRoofTrimRail) {
      continue;
    }

    // apply filter
    if (filter && !filter(roofTrimRail, otherRoofRoofTrimRail, isEnd, isOtherEnd)) {
      continue;
    }

    // add to connected rails
    connectedRails.push(otherRoofRoofTrimRail);
  }

  return connectedRails
    .filter(isNonVoidRoofTrimRailType)
    .sort((a, b) => polyLine3Length(b.rail) - polyLine3Length(a.rail));
}

function isRoofTrimRailConnected(options: ConnectedRoofTrimRailsOptions): boolean {
  const connectedRails = getConnectedRoofTrimRails(options);

  return connectedRails.length > 0;
}

export function isRoofTrimRailConnectedAtStart(
  roofTrimRail: RoofTrimRail,
  rtree: RBush3D,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): boolean {
  return isRoofTrimRailConnected({
    roofTrimRail,
    rtree,
    isEnd: false,
    boxHalfSize,
  });
}

export function isRoofTrimRailConnectedAtEnd(
  roofTrimRail: RoofTrimRail,
  rtree: RBush3D,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): boolean {
  return isRoofTrimRailConnected({
    roofTrimRail,
    rtree,
    isEnd: true,
    boxHalfSize,
  });
}

function coplanarFriezeRailsIntersectionZs(roofTrimRail: RoofTrimRail, otherRoofTrimRail: RoofTrimRail): number[] {
  if (!isRoofTrimRailTypeFrieze(roofTrimRail) || !isRoofTrimRailTypeFrieze(otherRoofTrimRail)) {
    return [];
  }
  const { rail: srcRail, friezePlane } = roofTrimRail;
  const { rail: otherRail } = otherRoofTrimRail;

  if (!srcRail || !otherRail) {
    return [];
  }

  // we know these are coplanar so no need to check
  const srcRail2 = planeProjectPolyLine3(friezePlane, srcRail);
  const otherRail2 = planeProjectPolyLine3(friezePlane, otherRail);

  const intResults = intersectPolyLine2PolyLine2(srcRail2, otherRail2);

  const zs: number[] = [];

  for (const intResult of intResults) {
    const { int } = intResult;
    if (int instanceof CurveCurvePointIntersection) {
      const { x, y } = int;
      const [, ytransformed] = planeProject3(friezePlane, x, y, 0);
      zs.push(ytransformed);
    } else if (int instanceof CurveCurveRangeIntersection) {
      const { startx, endx, starty, endy } = int;
      const [, startyTransformed] = planeProject3(friezePlane, startx, endx, 0);
      const [, endyTransformed] = planeProject3(friezePlane, starty, endy, 0);
      zs.push(startyTransformed, endyTransformed);
    }
  }

  return zs;
}

export function getIntersectingRoofTrimRailZLimit(
  roofTrimRail: RoofTrimRail,
  rtree: RBush3D,
  filter: ConnectedRoofTrimRailFilter,
  isEnd: boolean,
): number {
  if (isRoofTrimRailTypeVoid(roofTrimRail)) {
    return Number.NEGATIVE_INFINITY;
  }
  const connectedRails = getIntersectingRoofTrimRails({
    roofTrimRail,
    rtree,
    isEnd,
    filter, //assuming intersecting rails are within 20' of each other, should be an ok tolerance
  }).filter(isNonVoidRoofTrimRailType);

  if (connectedRails.length === 0) {
    return Number.NEGATIVE_INFINITY;
  }

  let zMax = Number.NEGATIVE_INFINITY;

  for (const otherRail of connectedRails) {
    const z = coplanarFriezeRailsIntersectionZs(roofTrimRail, otherRail);
    zMax = Math.max(zMax, ...z);
  }

  return zMax;
}

export function getRoofTrimCornerInfo(
  roofTrimRail: RoofTrimRail,
  rtree: RBush3D,
  isEnd: boolean,
  isAngledTrim: boolean,
): RoofRailConerInfo {
  if (isRoofTrimRailTypeVoid(roofTrimRail)) {
    return roofRailCornerInfoNew(RoofRailCornerType.NONE);
  }

  const isFasciaTrimRail = isRoofTrimRailTypeFascia(roofTrimRail);

  const { rail, roofPerimeterRail } = roofTrimRail;

  const [railX, railY, railZ] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

  const connectedRails = getConnectedRoofTrimRails({
    roofTrimRail,
    rtree,
    isEnd,
  }).filter(isNonVoidRoofTrimRailType);

  for (const { rail: otherRail, roofPerimeterRail: otherRoofPerimeterRail } of connectedRails) {
    const roofPerimeterRail1 = isEnd ? roofPerimeterRail : otherRoofPerimeterRail;
    const roofPerimeterRail2 = isEnd ? otherRoofPerimeterRail : roofPerimeterRail;
    const rail1 = isEnd ? rail : otherRail;
    const rail2 = isEnd ? otherRail : rail;

    const { edgePlane: edgePlane1, eaveCutType: eaveCutType1 } = roofPerimeterRail1;
    const { edgePlane: edgePlane2, eaveCutType: eaveCutType2 } = roofPerimeterRail2;

    const endRafterDir1 = getRoofPerimeterRailEndRafterDir(roofPerimeterRail1);
    const startRafterDir2 = getRoofPerimeterRailStartRafterDir(roofPerimeterRail2);

    const isEave1 = isEaveRoofPerimeterRail(roofPerimeterRail1);
    const isEave2 = isEaveRoofPerimeterRail(roofPerimeterRail2);

    const [x0, y0] = polyLine3StartPoint(rail1);
    const [x1, y1, z1] = polyLine3EndPoint(rail1);

    //the input point for the rail should be the same as the rail end point else continue
    if (
      vec3DistSquared(railX, railY, railZ, x1, y1, z1) >
        ROOF_RAIL_RTREE_BOX_HALF_WIDTH * ROOF_RAIL_RTREE_BOX_HALF_WIDTH &&
      isFasciaTrimRail
    ) {
      continue;
    }

    const [x2, y2] = polyLine3StartPoint(rail2);
    const [x3, y3] = polyLine3EndPoint(rail2);

    let plane1 = edgePlane1;
    let plane2 = edgePlane2;

    if (isAngledTrim && isEave1 && eaveCutType1 === EaveCutType.SQUARED) {
      const { uX, uY, uZ } = plane1;
      const [vX, vY, vZ] = vec3Cross(endRafterDir1[0], endRafterDir1[1], endRafterDir1[2], uX, uY, uZ);

      plane1 = planeNew(x1, y1, z1, uX, uY, uZ, vX, vY, vZ);
    }

    if (isAngledTrim && isEave2 && eaveCutType2 === EaveCutType.SQUARED) {
      const { uX, uY, uZ } = plane2;
      const [vX, vY, vZ] = vec3Cross(startRafterDir2[0], startRafterDir2[1], startRafterDir2[2], uX, uY, uZ);

      plane2 = planeNew(x1, y1, z1, uX, uY, uZ, vX, vY, vZ);
    }

    const [normal1X, normal1Y, normal1Z] = planeNormal(plane1);
    const [normal2X, normal2Y, normal2Z] = planeNormal(plane2);

    let [uX, uY, uZ] = vec3Add(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);
    [uX, uY, uZ] = vec3Normalize(uX, uY, uZ);
    let [vX, vY, vZ] = planeIsParallel(plane1, plane2)
      ? [plane1.vX, plane1.vY, plane1.vZ]
      : vec3Cross(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);
    [vX, vY, vZ] = vec3Normalize(vX, vY, vZ);

    if (vZ < 0) {
      [vX, vY, vZ] = vec3Negate(vX, vY, vZ);
    }

    const miterPlane = planeNew(x1, y1, z1, uX, uY, uZ, vX, vY, vZ);
    const angle = vec3AngleBetween(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);

    if (isEnd) {
      planeReverse(miterPlane);
    }

    if (roofPerimeterRail1.id === roofPerimeterRail2.id) {
      return roofRailCornerInfoNew(RoofRailCornerType.NONE);
    } else {
      // special case for almost collinear rails
      if (line2Collinear(x0, y0, x1, y1, x2, y2, x3, y3, DIST_EPSILON, ROOF_RAIL_COLLINEARITY_TOLERANCE)) {
        return roofRailCornerInfoNew(RoofRailCornerType.COPLANAR, otherRail, miterPlane, otherRoofPerimeterRail, angle);
      }

      const dir = line2Side(x0, y0, x1, y1, x3, y3);
      switch (dir) {
        case Line2Side.LEFT:
          return roofRailCornerInfoNew(
            RoofRailCornerType.OUTSIDE,
            otherRail,
            miterPlane,
            otherRoofPerimeterRail,
            angle,
          );
        case Line2Side.RIGHT:
          return roofRailCornerInfoNew(RoofRailCornerType.INSIDE, otherRail, miterPlane, otherRoofPerimeterRail, angle);
        default:
          return roofRailCornerInfoNew(
            RoofRailCornerType.COPLANAR,
            otherRail,
            miterPlane,
            otherRoofPerimeterRail,
            angle,
          );
      }
    }
  }

  return roofRailCornerInfoNew(RoofRailCornerType.NONE);
}

function getSoffitAngle(roofPerimeterRail: RoofPerimeterRail, edgeIndex: number): number {
  const { normals, rail } = roofPerimeterRail;

  const [tangentX, tangentY, tangentZ] = polyLine3EdgeTangent(rail, edgeIndex);
  const [normalX, normalY, normalZ] = normals[edgeIndex % normals.length];
  const [dirX, dirY, dirZ] = vec3Cross(tangentX, tangentY, tangentZ, normalX, normalY, normalZ);
  const [prjDirX, prjDirY, proDirZ] = vec3Normalize(dirX, dirY, 0);

  return vec3AngleBetween(dirX, dirY, dirZ, prjDirX, prjDirY, proDirZ);
}

interface EnclosingProfile {
  readonly innerPlane: Plane;
  readonly outerPlane: Plane;
  readonly midPlane: Plane;
  readonly innerTopOffsets: number[];
  readonly innerBottomOffsets: number[];
  readonly outerTopOffsets: number[];
  readonly outerBottomOffsets: number[];
}

function buildBoxedPlumbEnclosingProfile(
  roofPerimeterRail: RoofPerimeterRail,
  extendToSheathing: boolean,
): EnclosingProfile {
  const {
    rail,
    edgePlane,
    overhang,
    exteriorWallOffsetWithFinish,
    exteriorWallOffsetWithSheathing,
    fasciaBoardSize,
    soffitOffset,
    rafterDirections,
    fasciaBoardThickness,
    adjustFasciaBoardSize,
  } = roofPerimeterRail;

  const exteriorOffset = extendToSheathing ? exteriorWallOffsetWithSheathing : exteriorWallOffsetWithFinish;
  const soffitSize = fasciaBoardSize - soffitOffset;

  // build the refenece planes
  const outerPlane = planeMoveAlongNormal(planeCopy(edgePlane), -fasciaBoardThickness);
  const innerPlane = planeMoveAlongNormal(planeCopy(edgePlane), -(overhang - exteriorOffset));
  const midPlane = planeCopy(outerPlane);

  // build the offsets
  const edgeCount = polyLine3EdgeCount(rail);

  const innerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const innerTopOffsets = new Array<number>(edgeCount).fill(0);
  const outerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const outerTopOffsets = new Array<number>(edgeCount).fill(0);

  for (let i = 0; i < edgeCount; i++) {
    const [, , dirZ] = rafterDirections[i];
    const angle = getSoffitAngle(roofPerimeterRail, i);
    const beta = Math.PI / 2 - angle;
    const sign = dirZ > DIST_EPSILON ? 1 : -1;

    innerTopOffsets[i] = sign * Math.tan(angle) * (overhang - exteriorOffset);

    if (dirZ > DIST_EPSILON) {
      if (adjustFasciaBoardSize) {
        outerTopOffsets[i] = Math.tan(angle) * fasciaBoardThickness;

        outerBottomOffsets[i] = outerTopOffsets[i] + soffitSize / Math.cos(angle);
        innerBottomOffsets[i] = innerTopOffsets[i] + soffitSize / Math.sin(beta);
      } else {
        planeMoveAlongNormal(innerPlane, -fasciaBoardThickness);

        outerBottomOffsets[i] = outerTopOffsets[i] + soffitSize;
        innerBottomOffsets[i] = innerTopOffsets[i] + soffitSize;
      }
    }
  }

  return {
    outerPlane,
    innerPlane,
    midPlane,
    innerBottomOffsets,
    innerTopOffsets,
    outerBottomOffsets,
    outerTopOffsets,
  };
}

function buildBoxedSquaredEnclosingProfile(
  roofPerimeterRail: RoofPerimeterRail,
  extendToSheathing: boolean,
): EnclosingProfile {
  const {
    rail,
    edgePlane,
    overhang,
    exteriorWallOffsetWithFinish,
    exteriorWallOffsetWithSheathing,
    fasciaBoardSize,
    soffitOffset,
    rafterDirections,
    fasciaBoardThickness,
  } = roofPerimeterRail;

  const exteriorOffset = extendToSheathing ? exteriorWallOffsetWithSheathing : exteriorWallOffsetWithFinish;
  const soffitSize = fasciaBoardSize - soffitOffset;

  const { uX, uY, uZ } = edgePlane;
  const startRafterDir = getRoofPerimeterRailStartRafterDir(roofPerimeterRail);

  const [vX, vY, vZ] = vec3Cross(startRafterDir[0], startRafterDir[1], startRafterDir[2], uX, uY, uZ);

  // build the refenece planes
  const outerPlane = isEaveRoofPerimeterRail(roofPerimeterRail)
    ? planeNew(rail[0], rail[1], rail[2], uX, uY, uZ, vX, vY, vZ)
    : planeCopy(edgePlane);

  planeMoveAlongNormal(outerPlane, -fasciaBoardThickness);

  const innerPlane = planeMoveAlongNormal(planeCopy(edgePlane), -(overhang - exteriorOffset));
  const midPlane = planeCopy(outerPlane);

  // build the offsets
  const edgeCount = polyLine3EdgeCount(rail);

  const innerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const innerTopOffsets = new Array<number>(edgeCount).fill(0);
  const outerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const outerTopOffsets = new Array<number>(edgeCount).fill(0);

  for (let i = 0; i < edgeCount; i++) {
    const [, , dirZ] = rafterDirections[i];
    const angle = getSoffitAngle(roofPerimeterRail, i);
    const beta = Math.PI / 2 - angle;
    const sign = dirZ > DIST_EPSILON ? 1 : -1;

    innerTopOffsets[i] = sign * Math.tan(angle) * (overhang - exteriorOffset);
    if (planeIsParallel(innerPlane, outerPlane)) {
      innerBottomOffsets[i] = soffitSize;
    } else if (dirZ < -DIST_EPSILON) {
      innerBottomOffsets[i] = soffitSize * Math.sin(beta) - fasciaBoardThickness * Math.sin(angle);
    } else if (dirZ > DIST_EPSILON) {
      innerBottomOffsets[i] = innerTopOffsets[i] + soffitSize / Math.sin(beta);
    }
  }

  return {
    outerPlane,
    innerPlane,
    midPlane,
    innerBottomOffsets,
    innerTopOffsets,
    outerBottomOffsets,
    outerTopOffsets,
  };
}

function buildEnclosedOpenPlumbEnclosingProfile(
  roofPerimeterRail: RoofPerimeterRail,
  extendToSheathing: boolean,
): EnclosingProfile {
  const {
    rail,
    edgePlane,
    overhang,
    exteriorWallOffsetWithFinish,
    exteriorWallOffsetWithSheathing,
    fasciaBoardSize,
    soffitOffset,
    rafterDirections,
    fasciaBoardThickness,
  } = roofPerimeterRail;

  const exteriorOffset = extendToSheathing ? exteriorWallOffsetWithSheathing : exteriorWallOffsetWithFinish;
  const soffitSize = fasciaBoardSize - soffitOffset;

  // build the refenece planes
  const outerPlane = planeMoveAlongNormal(planeCopy(edgePlane), -fasciaBoardThickness);
  const innerPlane = planeMoveAlongNormal(planeCopy(edgePlane), -(overhang - exteriorOffset));
  const midPlane = planeCopy(outerPlane);

  // build the offsets
  const edgeCount = polyLine3EdgeCount(rail);

  const innerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const innerTopOffsets = new Array<number>(edgeCount).fill(0);
  const outerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const outerTopOffsets = new Array<number>(edgeCount).fill(0);

  let minZ = Number.POSITIVE_INFINITY;
  let validMinZ = false;

  for (let i = 0; i < edgeCount; i++) {
    const [, , dirZ] = rafterDirections[i];
    const angle = getSoffitAngle(roofPerimeterRail, i);
    const beta = Math.PI / 2 - angle;
    const sign = dirZ > DIST_EPSILON ? 1 : -1;
    const halfAngle = angle / 2;
    const d = soffitSize / Math.cos(halfAngle);
    const z = d * Math.sin(halfAngle);

    if (dirZ < -DIST_EPSILON) {
      minZ = Math.min(minZ, z);
      validMinZ = true;
    }

    if (dirZ > DIST_EPSILON) {
      outerTopOffsets[i] = sign * Math.tan(angle) * fasciaBoardThickness;
      outerBottomOffsets[i] = outerTopOffsets[i] + soffitSize / Math.cos(angle);
    }

    innerTopOffsets[i] = sign * Math.tan(angle) * (overhang - exteriorOffset);

    if (Math.abs(dirZ) > DIST_EPSILON) {
      innerBottomOffsets[i] = innerTopOffsets[i] + soffitSize / Math.sin(beta);
    }
  }

  if (validMinZ) {
    planeMoveAlongNormal(midPlane, Math.min(-minZ + fasciaBoardThickness, 0));
  }

  return {
    outerPlane,
    innerPlane,
    midPlane,
    innerBottomOffsets,
    innerTopOffsets,
    outerBottomOffsets,
    outerTopOffsets,
  };
}

function buildEnclosedOpenSquaredEnclosingProfile(
  roofPerimeterRail: RoofPerimeterRail,
  extendToSheathing: boolean,
): EnclosingProfile {
  const {
    rail,
    edgePlane,
    overhang,
    exteriorWallOffsetWithFinish,
    exteriorWallOffsetWithSheathing,
    fasciaBoardSize,
    soffitOffset,
    rafterDirections,
    fasciaBoardThickness,
  } = roofPerimeterRail;

  const exteriorOffset = extendToSheathing ? exteriorWallOffsetWithSheathing : exteriorWallOffsetWithFinish;
  const soffitSize = fasciaBoardSize - soffitOffset;

  const { uX, uY, uZ } = edgePlane;
  const startRafterDir = getRoofPerimeterRailStartRafterDir(roofPerimeterRail);

  const [vX, vY, vZ] = vec3Cross(startRafterDir[0], startRafterDir[1], startRafterDir[2], uX, uY, uZ);

  // build the refenece planes
  const outerPlane = isEaveRoofPerimeterRail(roofPerimeterRail)
    ? planeNew(rail[0], rail[1], rail[2], uX, uY, uZ, vX, vY, vZ)
    : planeCopy(edgePlane);

  planeMoveAlongNormal(outerPlane, -fasciaBoardThickness);

  const innerPlane = planeMoveAlongNormal(planeCopy(edgePlane), -(overhang - exteriorOffset));
  const midPlane = planeCopy(outerPlane);

  // build the offsets
  const edgeCount = polyLine3EdgeCount(rail);

  const innerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const innerTopOffsets = new Array<number>(edgeCount).fill(0);
  const outerBottomOffsets = new Array<number>(edgeCount).fill(soffitSize);
  const outerTopOffsets = new Array<number>(edgeCount).fill(0);

  for (let i = 0; i < edgeCount; i++) {
    const [, , dirZ] = rafterDirections[i];
    const angle = getSoffitAngle(roofPerimeterRail, i);
    const beta = Math.PI / 2 - angle;
    const sign = dirZ > DIST_EPSILON ? 1 : -1;

    innerTopOffsets[i] = sign * Math.tan(angle) * (overhang - exteriorOffset);

    if (Math.abs(dirZ) > DIST_EPSILON) {
      innerBottomOffsets[i] = innerTopOffsets[i] + soffitSize / Math.sin(beta);
    }
  }

  return {
    outerPlane,
    innerPlane,
    midPlane,
    innerBottomOffsets,
    innerTopOffsets,
    outerBottomOffsets,
    outerTopOffsets,
  };
}

export function buildEnclosingRail(
  roofPerimeterRail: RoofPerimeterRail,
  extendToSheathing: boolean,
  groupId: string = "",
): SoffitRail | RoofTrimRailVoid {
  const {
    rail,
    eaveType,
    eaveCutType,
    soffitMaterial,
    fasciaBoardSize,
    soffitOffset,
    fasciaBoardThickness,
    prevPerimeterRail,
    nextPerimeterRail,
  } = roofPerimeterRail;

  if (!rail) {
    return roofTrimRailVoidNew();
  }

  let enclosingProfile: EnclosingProfile | undefined;

  if (isAngledEave[eaveType]) {
    if (eaveCutType === EaveCutType.PLUMB) {
      enclosingProfile = buildEnclosedOpenPlumbEnclosingProfile(roofPerimeterRail, extendToSheathing);
    } else {
      enclosingProfile = buildEnclosedOpenSquaredEnclosingProfile(roofPerimeterRail, extendToSheathing);
    }
  } else {
    if (eaveCutType === EaveCutType.PLUMB) {
      enclosingProfile = buildBoxedPlumbEnclosingProfile(roofPerimeterRail, extendToSheathing);
    } else {
      enclosingProfile = buildBoxedSquaredEnclosingProfile(roofPerimeterRail, extendToSheathing);
    }
  }

  if (!enclosingProfile) {
    return roofTrimRailVoidNew();
  }

  // corner types
  const startCornerInfo = getRoofCornerInfo(roofPerimeterRail, false, true);
  const endCornerInfo = getRoofCornerInfo(roofPerimeterRail, true, true);

  const { type: startCornerType } = startCornerInfo;
  const { type: endCornerType } = endCornerInfo;

  // get slopes
  const signedStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
  const signedEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

  const absStartSlope = Math.abs(signedStartSlope);
  const absEndSlope = Math.abs(signedEndSlope);

  const soffitSize = fasciaBoardSize - soffitOffset;

  // get the soffit profile data
  const { outerPlane, innerPlane, midPlane, outerTopOffsets, outerBottomOffsets, innerTopOffsets, innerBottomOffsets } =
    enclosingProfile;

  // build the fascia top rail
  const fasciaTopRail = planeProjectPolyLine3(outerPlane, rail);

  // build the outer rails
  let outerTopRail = polyLine2Copy(fasciaTopRail);
  let outerBottomRail = polyLine2Copy(fasciaTopRail);

  // build the inner rails
  let innerTopRail = planeProjectPolyLine3(innerPlane, rail);
  let innerBottomRail = polyLine2Copy(innerTopRail);

  // apply extensions
  let startExtension = 0;

  if (startCornerType === RoofRailCornerType.OUTSIDE) {
    startExtension -= fasciaBoardThickness / Math.cos(Math.atan(absStartSlope));
  }

  if (startCornerType === RoofRailCornerType.INSIDE && prevPerimeterRail) {
    const horizontalExtension = prevPerimeterRail.overhang - prevPerimeterRail.exteriorWallOffsetWithFinish;
    startExtension += horizontalExtension / Math.cos(Math.atan(absStartSlope));
  }

  if (Math.abs(startExtension) > DIST_EPSILON) {
    polyLine2ExtendStart(outerTopRail, startExtension);
    polyLine2ExtendStart(outerBottomRail, startExtension);
    polyLine2ExtendStart(innerTopRail, startExtension);
    polyLine2ExtendStart(innerBottomRail, startExtension);
  }

  let endExtension = 0;

  if (endCornerType === RoofRailCornerType.OUTSIDE) {
    endExtension -= fasciaBoardThickness / Math.cos(Math.atan(absEndSlope));
  }

  if (endCornerType === RoofRailCornerType.INSIDE && nextPerimeterRail) {
    const horizontalExtension = nextPerimeterRail.overhang - nextPerimeterRail.exteriorWallOffsetWithFinish;
    endExtension += horizontalExtension / Math.cos(Math.atan(absEndSlope));
  }

  if (Math.abs(endExtension) > DIST_EPSILON) {
    polyLine2ExtendEnd(outerTopRail, endExtension);
    polyLine2ExtendEnd(outerBottomRail, endExtension);
    polyLine2ExtendEnd(innerTopRail, endExtension);
    polyLine2ExtendEnd(innerBottomRail, endExtension);
  }

  // offset the rails
  const edgeCount = polyLine3EdgeCount(rail);

  if (!(edgeCount === outerTopOffsets.length)) {
    console.assert(false);
  }
  if (!(edgeCount === outerBottomOffsets.length)) {
    console.assert(false);
  }
  if (!(edgeCount === innerTopOffsets.length)) {
    console.assert(false);
  }
  if (!(edgeCount === innerBottomOffsets.length)) {
    console.assert(false);
  }

  outerTopRail = offsetPolyLine2Variable(outerTopRail, outerTopOffsets);
  outerBottomRail = offsetPolyLine2Variable(outerBottomRail, outerBottomOffsets);
  innerTopRail = offsetPolyLine2Variable(innerTopRail, innerTopOffsets);
  innerBottomRail = offsetPolyLine2Variable(innerBottomRail, innerBottomOffsets);

  // adjust the start and end point of the bottom rails (if needed)
  if (eaveCutType === EaveCutType.PLUMB) {
    const startExtension = startCornerType !== RoofRailCornerType.COPLANAR ? soffitSize * signedStartSlope : 0;
    const endExtension = endCornerType !== RoofRailCornerType.COPLANAR ? soffitSize * signedEndSlope : 0;

    // apply first the biggest extension, to avoid negative extensions
    // to invert the direction of the rail
    if (startExtension > endExtension) {
      polyLine2ExtendStart(outerBottomRail, startExtension);
      polyLine2ExtendStart(innerBottomRail, startExtension);

      polyLine2ExtendEnd(outerBottomRail, endExtension);
      polyLine2ExtendEnd(innerBottomRail, endExtension);
    } else {
      polyLine2ExtendEnd(outerBottomRail, endExtension);
      polyLine2ExtendEnd(innerBottomRail, endExtension);

      polyLine2ExtendStart(outerBottomRail, startExtension);
      polyLine2ExtendStart(innerBottomRail, startExtension);
    }
  }

  // bottom extensions
  const { startExtension: startBottomExtension, endExtension: endBottomExtension } = getCollinearRoofTrimExtensions({
    rail,
    roofPerimeterRail,
    startCornerInfo,
    endCornerInfo,
    profileSize: soffitSize,
    filter: soffitCollinearExtensionFilter,
  });

  polyLine2ExtendStart(innerBottomRail, startBottomExtension);
  polyLine2ExtendStart(outerBottomRail, startBottomExtension);

  polyLine2ExtendEnd(innerBottomRail, endBottomExtension);
  polyLine2ExtendEnd(outerBottomRail, endBottomExtension);

  // inner top extensions
  const { startExtension: startTopExtension } = getCollinearRoofTrimExtensions({
    rail,
    roofPerimeterRail,
    startCornerInfo,
    endCornerInfo,
    profileSize: innerTopOffsets[0],
    filter: soffitCollinearExtensionFilter,
  });

  const { endExtension: endTopExtension } = getCollinearRoofTrimExtensions({
    rail,
    roofPerimeterRail,
    startCornerInfo,
    endCornerInfo,
    profileSize: innerTopOffsets[innerTopOffsets.length - 1],
    filter: soffitCollinearExtensionFilter,
  });

  polyLine2ExtendStart(innerTopRail, startTopExtension);
  polyLine2ExtendEnd(innerTopRail, endTopExtension);

  // build the mid rail
  const midBottomRail = planeReProjectPolyLine2(outerBottomRail, outerPlane, midPlane);

  // create a new handle for the soffit that will be generated from this rail
  const soffitHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: {
      entityType: "ROOF_SOFFIT",
      id: uid(),
    },
  };

  return roofSoffitRailNew(
    rail,
    roofPerimeterRail,
    outerPlane,
    midPlane,
    innerPlane,
    fasciaTopRail,
    outerTopRail,
    outerBottomRail,
    midBottomRail,
    innerTopRail,
    innerBottomRail,
    soffitSize,
    groupId,
    soffitHandle,
    soffitMaterial,
  );
}

export function buildSoffitMiterPlane(
  cornerType: RoofRailCornerType,
  soffitRail: SoffitRail,
  isEnd: boolean,
  neighborRail: RoofPerimeterRail,
): Plane | null {
  if (!isRoofRailCorner(cornerType) || !neighborRail) {
    return null;
  }

  const neighorSoffit = buildEnclosingRail(neighborRail, true);
  if (!isRoofTrimRailTypeSoffit(neighorSoffit)) {
    return null;
  }

  const { outerPlane, innerPlane, fasciaTopRail, soffitSize } = soffitRail;
  const { outerPlane: neighborOuterPlane, innerPlane: neighborInnerPlane } = neighorSoffit;

  const innerPlane1 = isEnd ? innerPlane : neighborInnerPlane;
  const innerPlane2 = isEnd ? neighborInnerPlane : innerPlane;

  const outerPlane1 = isEnd ? outerPlane : neighborOuterPlane;
  const outerPlane2 = isEnd ? neighborOuterPlane : outerPlane;

  const int1 = intersectPlanePlane(innerPlane1, innerPlane2);
  if (!int1) {
    return null;
  }

  const int2 = intersectPlanePlane(outerPlane1, outerPlane2);
  if (!int2) {
    return null;
  }

  let plane: Plane | null = null;
  if (cornerType === RoofRailCornerType.INSIDE) {
    const [uX, uY, uZ] = vec3Normalize(int2.originX - int1.originX, int2.originY - int1.originY, 0);
    plane = planeNew(int2.originX, int2.originY, 0, uX, uY, uZ, 0, 0, 1);
  } else {
    if (!(cornerType === RoofRailCornerType.OUTSIDE)) {
      console.assert(false);
    }
    const topRails3 = polyLine2OnPlane(outerPlane, fasciaTopRail);
    const [topX, topY, topZ] = isEnd ? polyLine3EndPoint(topRails3) : polyLine3StartPoint(topRails3);

    const [x2, y2, z2] = vec3AddScale(topX, topY, topZ, int2.dirX, int2.dirY, int2.dirZ, -soffitSize);
    const [x1, y1, z1] = [int1.originX, int1.originY, z2];

    const [uX, uY, uZ] = vec3Normalize(x2 - x1, y2 - y1, z2 - z1);
    plane = planeNew(x2, y2, z2, uX, uY, uZ, 0, 0, 1);
  }

  if (isEnd && plane) {
    planeReverse(plane);
  }

  return plane;
}

function getSoffitInnerBottomHeight(soffitRail: SoffitRail, isEnd: boolean): number {
  const { innerBottomRail, innerPlane } = soffitRail;
  const innerBottomRail3 = polyLine2OnPlane(innerPlane, innerBottomRail);

  const [, , z] = isEnd ? polyLine3EndPoint(innerBottomRail3) : polyLine3StartPoint(innerBottomRail3);

  return z;
}

export function isEaveRoofPerimeterRail(roofPerimeterRail: RoofPerimeterRail): boolean {
  const { rail } = roofPerimeterRail;

  const [, , startZ] = polyLine3StartPoint(rail);
  const [, , endZ] = polyLine3EndPoint(rail);

  if (Math.abs(endZ - startZ) > DIST_EPSILON) {
    return false;
  }

  const startRafterDir = getRoofPerimeterRailStartRafterDir(roofPerimeterRail);
  const endRafterDir = getRoofPerimeterRailEndRafterDir(roofPerimeterRail);

  return (
    vec3Dot(
      startRafterDir[0],
      startRafterDir[1],
      startRafterDir[2],
      endRafterDir[0],
      endRafterDir[1],
      endRafterDir[2],
    ) > ONE_MINUS_DIST_EPSILON
  );
}

interface RoofTrimExtensions {
  readonly startExtension: number;
  readonly endExtension: number;
}

interface GetCollinearRoofTrimExtensionsOptions {
  readonly rail: PolyLine3;
  readonly roofPerimeterRail: RoofPerimeterRail;
  readonly startCornerInfo: RoofRailConerInfo;
  readonly endCornerInfo: RoofRailConerInfo;
  readonly profileSize: number;
  readonly filter?: (
    roofPerimeterRail: RoofPerimeterRail,
    otherRoofPerimeterRail: RoofPerimeterRail,
    isEnd: boolean,
  ) => boolean;
}

export function getCollinearRoofTrimExtensions({
  rail,
  roofPerimeterRail,
  startCornerInfo,
  endCornerInfo,
  profileSize,
  filter = (_roofPerimeterRail: RoofPerimeterRail, _otherRoofPerimeterRail: RoofPerimeterRail, _isEnd: boolean) => true,
}: GetCollinearRoofTrimExtensionsOptions): RoofTrimExtensions {
  const { type: startCornerType, otherPerimeterRail: prevRoofPerimeterRail } = startCornerInfo;
  const { type: endCornerType, otherPerimeterRail: nextRoofPerimeterRail } = endCornerInfo;

  let startExtension = 0;
  let endExtension = 0;

  if (
    startCornerType === RoofRailCornerType.COPLANAR &&
    prevRoofPerimeterRail &&
    filter(roofPerimeterRail, prevRoofPerimeterRail, false)
  ) {
    const { rail: prevRail } = prevRoofPerimeterRail;
    const signedStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
    const prevSlope = polyLine3EdgeSlopeInZ(prevRail, polyLine3EdgeCount(prevRail) - 1);

    const angle = Math.atan(signedStartSlope);
    const prevAngle = Math.atan(prevSlope);

    const angleDiff = angle - prevAngle;

    startExtension = profileSize * Math.tan(angleDiff / 2);
  }

  if (
    endCornerType === RoofRailCornerType.COPLANAR &&
    nextRoofPerimeterRail &&
    filter(roofPerimeterRail, nextRoofPerimeterRail, true)
  ) {
    const { rail: nextRail } = nextRoofPerimeterRail;
    const signedEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);
    const nextSlope = -polyLine3EdgeSlopeInZ(nextRail, 0);

    const angle = Math.atan(signedEndSlope);
    const nextAngle = Math.atan(nextSlope);

    const angleDiff = angle - nextAngle;

    endExtension = profileSize * Math.tan(angleDiff / 2);
  }

  return {
    startExtension,
    endExtension,
  };
}

interface RoofTrimCollinearMiterPlanes {
  readonly startPlane: Plane | null;
  readonly endPlane: Plane | null;
}

interface GetCollinearRoofTrimMiterPlanesOptions {
  readonly rail: PolyLine3;
  readonly plane: Plane;
  readonly startCornerInfo: RoofRailConerInfo;
  readonly endCornerInfo: RoofRailConerInfo;
}

export function getCollinearRoofTrimMiterPlanes({
  rail,
  plane,
  startCornerInfo,
  endCornerInfo,
}: GetCollinearRoofTrimMiterPlanesOptions): RoofTrimCollinearMiterPlanes {
  const { type: startCornerType, neighborRail: prevRail } = startCornerInfo;
  const { type: endCornerType, neighborRail: nextRail } = endCornerInfo;

  const [uX, uY, uZ] = planeNormal(plane);

  let startPlane: Plane | null = null;
  let endPlane: Plane | null = null;

  if (startCornerType === RoofRailCornerType.COPLANAR && prevRail) {
    const [x, y, z] = polyLine3StartPoint(rail);
    const signedStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
    const prevSlope = polyLine3EdgeSlopeInZ(prevRail, polyLine3EdgeCount(prevRail) - 1);

    const angle = Math.atan(signedStartSlope);
    const prevAngle = Math.atan(prevSlope);
    const midAngle = (angle + prevAngle) / 2;

    const rotatedEdgePlane = planeRotateAroundNormal(planeCopy(plane), midAngle);
    const { vX, vY, vZ } = rotatedEdgePlane;

    startPlane = planeNew(x, y, z, uX, uY, uZ, vX, vY, vZ);
  }

  if (endCornerType === RoofRailCornerType.COPLANAR && nextRail) {
    const [x, y, z] = polyLine3EndPoint(rail);
    const signedEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);
    const nextSlope = -polyLine3EdgeSlopeInZ(nextRail, 0);

    const angle = Math.atan(signedEndSlope);
    const nextAngle = Math.atan(nextSlope);
    const midAngle = (angle + nextAngle) / 2;

    const rotatedEdgePlane = planeRotateAroundNormal(planeCopy(plane), -midAngle);
    const { vX, vY, vZ } = rotatedEdgePlane;

    endPlane = planeNew(x, y, z, -uX, -uY, -uZ, vX, vY, vZ);
  }

  return {
    startPlane,
    endPlane,
  };
}

export function soffitCollinearExtensionFilter(
  roofPerimeterRail: RoofPerimeterRail,
  otherRoofPerimeterRail: RoofPerimeterRail,
  isEnd: boolean,
): boolean {
  const { edgePlane: edgePlane, eaveCutType: eaveCutType } = roofPerimeterRail;
  const { edgePlane: otherEdgePlane, eaveCutType: otherEaveCutType } = otherRoofPerimeterRail;

  const isEave = isEaveRoofPerimeterRail(roofPerimeterRail);
  const otherIsEave = isEaveRoofPerimeterRail(otherRoofPerimeterRail);

  const rafterDir = isEnd
    ? getRoofPerimeterRailEndRafterDir(roofPerimeterRail)
    : getRoofPerimeterRailStartRafterDir(roofPerimeterRail);
  const otherRafterDir = isEnd
    ? getRoofPerimeterRailStartRafterDir(otherRoofPerimeterRail)
    : getRoofPerimeterRailEndRafterDir(otherRoofPerimeterRail);

  const [nX, nY, nZ] = isEave && eaveCutType === EaveCutType.SQUARED ? rafterDir : planeNormal(edgePlane);
  const [otherNX, otherNY, otherNZ] =
    otherIsEave && otherEaveCutType === EaveCutType.SQUARED ? otherRafterDir : planeNormal(otherEdgePlane);

  return vec3Dot(nX, nY, nZ, otherNX, otherNY, otherNZ) > ONE_MINUS_DIST_EPSILON;
}

function flattenRailToMinZ(rail: PolyLine3): PolyLine3 {
  const l = rail.length;
  if (l < 6) {
    return rail;
  }

  let minZ = rail[2];
  for (let i = 5; i < l; i += 3) {
    minZ = Math.min(minZ, rail[i]);
  }

  for (let i = 2; i < l; i += 3) {
    rail[i] = minZ;
  }

  return rail;
}

/**
The following correction is required on offsetted rail because the offsetted rail looses its connection from left and right vertical planes
 */
function alignBeginAndEndPointsOfOffsettedRail(mainRail: PolyLine3, offsettedRail: PolyLine3): void {
  const length = offsettedRail.length;

  if (mainRail.length !== length || length < 6) {
    return;
  }

  if (length >= 6) {
    const startDx = offsettedRail[0] - offsettedRail[3];
    const startDy = offsettedRail[1] - offsettedRail[4];
    const startDz = offsettedRail[2] - offsettedRail[5];
    const [startNormX, startNormY, startNormZ] = vec3Normalize(startDx, startDy, startDz);
    const intersectStartVec3 = intersectRay3PlaneWithNormal(
      offsettedRail[0],
      offsettedRail[1],
      offsettedRail[2],
      startNormX,
      startNormY,
      startNormZ,
      mainRail[0],
      mainRail[1],
      mainRail[2],
      startNormX,
      startNormY,
      0,
    );
    if (intersectStartVec3) {
      const [intersectX, intersectY, intersectZ] = intersectStartVec3;
      offsettedRail[0] = intersectX;
      offsettedRail[1] = intersectY;
      offsettedRail[2] = intersectZ;
    }

    const endDx = offsettedRail[length - 3] - offsettedRail[length - 6];
    const endDy = offsettedRail[length - 2] - offsettedRail[length - 5];
    const endDz = offsettedRail[length - 1] - offsettedRail[length - 4];
    const [endNormX, endNormY, endNormZ] = vec3Normalize(endDx, endDy, endDz);
    const intersectEndVec3 = intersectRay3PlaneWithNormal(
      offsettedRail[length - 3],
      offsettedRail[length - 2],
      offsettedRail[length - 1],
      endNormX,
      endNormY,
      endNormZ,
      mainRail[length - 3],
      mainRail[length - 2],
      mainRail[length - 1],
      endNormX,
      endNormY,
      0,
    );
    if (intersectEndVec3) {
      const [intersectX, intersectY, intersectZ] = intersectEndVec3;
      offsettedRail[length - 3] = intersectX;
      offsettedRail[length - 2] = intersectY;
      offsettedRail[length - 1] = intersectZ;
    }
  }
}
